import { faSquareArrowUpRight } from '@elynx/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateRange, DisplaySettings, Undo } from '@mui/icons-material';
import { AutocompleteChangeDetails, AutocompleteChangeReason, Box, Button, Fab, FormLabel, Paper, Stack, useTheme } from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CellEditingStartedEvent, CellEditingStoppedEvent, CellValueChangedEvent, ColDef, EditableCallbackParams, GetRowIdParams, GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { addDays } from 'date-fns';
import Enumerable from 'linq';
import _ from 'lodash';
import { ChangeEvent, forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { AccessFunctionalityTypeEnum } from '../../common/enums/AccessFunctionalityTypeEnum';
import { AccessTypeEnum } from '../../common/enums/AccessTypeEnum';
import { ApplicationMimeTypesEnum } from '../../common/enums/ApplicationMimeTypesEnum';
import { DataTypeEnum } from '../../common/enums/DataTypeEnum';
import { DeviceTypesEnum } from '../../common/enums/DeviceTypesEnum';
import { AccessType } from '../../common/enums/DRCXEnums';
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { NavigationSource } from '../../common/enums/NavigationSource';
import { NewViewTypeEnum } from '../../common/enums/NewViewTypeEnum';
import { ObjectFeaturesEnum } from '../../common/enums/ObjectFeaturesEnum';
import { SetPointEventTypeEnum } from '../../common/enums/SetPointEventTypeEnum';
import { TimeframeTypeEnum } from '../../common/enums/TimeframeTypeEnum';
import { UIRefreshCategoryEnum } from '../../common/enums/UIRefreshCategoryEnum';
import { ActionTypeEnum, StatusEnum } from '../../common/IExportNotificationMsg';
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IApiProblemDetails } from '../../common/models/IApiProblemDetails';
import { IEntityValueDeviceModel, IEntityValueTagModel, IEntityValueTankModel } from '../../common/models/IEntityValueModels';
import { ISortParameters } from '../../common/models/ISortParameters';
import { ALL_TAGS_CATEGORY_ID, ITagCategory, SUMMARY_LISTING_TAGS_CATEGORY_ID } from '../../common/models/ITagCategory';
import { ITagModel } from '../../common/models/ITagModel';
import { ITagValue } from '../../common/models/ITagValue';
import { getTotalFromFeetAndInchesText } from '../../common/utilities/FeetAndInches';
import { getAccessText } from '../../common/utilities/GetAccessText';
import { hasPermission } from '../../common/utilities/HasPermission';
import { getHoursMinutesSecondsText } from '../../common/utilities/HoursMinutesSeconds';
import { sleep } from "../../common/utilities/Sleep";
import { getTimeframeTimeDisplayText } from '../../common/utilities/TimeframeUtilities';
import UUID from '../../common/utilities/Uuid';
import { AppSettingsActionTypeEnum, useAppSettings } from '../../contexts/app-context/AppContext';
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useLiveDataContext } from "../../contexts/live-data-context/UseLiveDataContext";
import { LiveDataStateItem } from "../../contexts/live-data-context/LiveDataContextTypes";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useProgressContext } from '../../contexts/progress-context/ProgressContext';
import { useUserContext } from '../../contexts/user-context/UserContext';
import { useAutoRefresh } from '../../hooks/use-auto-refresh/UseAutoRefresh';
import useNavigateTo, { NavigateToOptions } from '../../hooks/use-navigate-to/useNavigateTo';
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useDeleteObjectTagValuesConfiguration } from '../../queries/object-tag-values/UseDeleteObjectTagValuesConfiguration';
import { useModifyObjectTagValuesConfigurationAccess } from '../../queries/object-tag-values/UseModifyObjectTagValuesConfigurationAccess';
import { useObjectTagValuesConfigurationDefaultOptions } from '../../queries/object-tag-values/UseObjectTagValuesConfigurationDefaultOptions';
import { useObjectTagValuesConfigurations } from '../../queries/object-tag-values/UseObjectTagValuesConfigurations';
import { useRenameObjectTagValuesConfiguration } from '../../queries/object-tag-values/UseRenameObjectTagValuesConfiguration';
import { useSaveObjectTagValuesDefault } from '../../queries/object-tag-values/UseSaveObjectTagValuesDefault';
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useTagValues } from "../../queries/tag-values/UseTagValues";
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { IObjectTagValuesDashboardComponent } from '../../services/dashboard-data-service/Models';
import { CommandStatusEnum, LiveDataCommandEnum, LiveDataCommandStateEnum } from '../../services/live-data-service/Models';
import { ObjectTagValuesConfigurationDescription } from '../../services/object-tag-values-data-service/ConfigurationModels';
import OfflineDataService from '../../services/offline-data-service/OfflineDataService';
import { RestError } from "../../services/rest-service/RestService";
import { IGroupViewState } from '../../services/state-data-service/Models';
import storageService from '../../services/storage/storage-service/StorageService';
import TagValueDataService from '../../services/tag-value-data-service/TagValueDataService';
import { ITrendingDisplayOptions } from '../../services/trending-data-service/Models';
import AccessLevelDialog, { bindAccessLevelDialog, useAccessLevelDialogState } from "../access-level-dialog/AccessLevelDialog";
import { ActionMenuItem, BaseActionEnum } from '../action-menu/ActionMenuItem';
import { ConfigurationActionEnum, ConfigurationActionItem, configurationActionItems } from '../action-menu/ConfigurationActions';
import { FeetAndInchesCellEditor } from '../ag-grid-extensions/cell-editors/FeetAndInchesCellEditor';
import { FeetAndInchesMaskedCellEditor } from '../ag-grid-extensions/cell-editors/FeetAndInchesMaskedCellEditor';
import { NumericCellEditor } from '../ag-grid-extensions/cell-editors/NumericCellEditor';
import { TimeCellEditor } from '../ag-grid-extensions/cell-editors/TimeCellEditor';
import { GridData } from '../ag-grid-extensions/models/GridData';
import { RowDef } from "../ag-grid-extensions/models/RowDef";
import TagNameRenderer from '../ag-grid-extensions/renderers/TagNameRenderer';
import TagValueRenderer from '../ag-grid-extensions/renderers/TagValueRenderer';
import { bindConfirmWriteDownDialog, ConfirmWriteDownDialog, useConfirmWriteDownDialogState } from '../confirm-writedown-dialog/ConfirmWriteDownDialog';
import { ConfirmationDialogResultReason } from '../confirmation-dialog/ConfirmationDialog';
import { ContentContainer } from '../content-container/ContentContainer';
import { ContentSection } from '../content-section/ContentSection';
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import { bindCustomDurationDialog, CustomDurationDialog, CustomDurationDialogValues, useCustomDurationDialogState } from '../custom-duration-dialog/CustomDurationDialog';
import { DurationOptionEnum } from '../custom-duration-dialog/CustomDurationUtils';
import DefaultGrid from "../default-grid/DefaultGrid";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from '../no-data/NoData';
import { bindObjectGraphOptionsDialog, ObjectGraphOptionsDialog, useObjectGraphOptionsDialogState } from '../object-graph/options-dialog/ObjectGraphOptionsDialog';
import { ObjectGraphOptionsDialogValues } from '../object-graph/options-dialog/ObjectGraphOptionsUtils';
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import RenameConfigurationDialog, { bindRenameConfigurationDialog, useRenameConfigurationDialogState } from "../rename-configuration-dialog/RenameConfigurationDialog";
import SetAsDefaultDialog, { bindDefaultConfigurationDialog, useDefaultConfigurationDialogState } from "../set-as-default-dialog/SetAsDefaultDialog";
import SideSheetCheckbox from '../side-sheet/side-sheet-checkbox/SideSheetCheckbox';
import SideSheetGroupedSelect, { SideSheetGroupedSelectOption } from '../side-sheet/side-sheet-grouped-select/SideSheetGroupedSelect';
import SideSheetMultiSelect from '../side-sheet/side-sheet-multiselect/SideSheetMultiSelect';
import SideSheetRadioGroup from '../side-sheet/side-sheet-radio-group/SideSheetRadioGroup';
import SideSheetSwitch from '../side-sheet/side-sheet-switch/SideSheetSwitch';
import TankCylinderV2 from '../tank-cylinder-v2/TankCylinderV2';
import TankLevelGraph, { TankLevelGraphType } from '../tank-level-graph/TankLevelGraph';
import ObjectTagValuesConfigurationEditorDialog, { bindObjectTagValuesConfigurationEditorDialog, ObjectTagValuesConfigurationEditorDialogResult, useObjectTagValuesConfigurationEditorDialogState } from './configuration-editor/ObjectTagValuesConfigurationEditorDialog';
import ObjectGroupingCellRenderer from './ObjectGroupingCellRenderer';
import ObjectTagValuesChartList from './ObjectTagValuesChartList';
import useObjectTagValueState, { OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY, ObjectTagValuesColumnOptionsState, ObjectTagValuesViewEnum } from './ObjectTagValuesState';
import ObjectTagValueUtils, { UNGROUPED_CATEGORY_ID, writePermission } from './ObjectTagValuesUtils';
import WriteDownFormDialog, { bindWriteDownFormDialog, useWriteDownFormDialogState } from './WriteDownFormDialog';
import { useEvaluateFeature } from '../../queries/features/UseEvaluateFeature';
import { FeatureConstants } from '../../services/features-data-service/Models';
import { useConfigurationsContext } from '../../contexts/configurations-context/UseConfigurationsContext';
import { subscribe, unsubscribe } from 'pubsub-js';
import { CONFIGURATION_CHANGED_TOPIC, ConfigurationChangedMsg } from '../../common/models/pub-sub/PubSubTopics';
import { AutoSizeStrategyEnum } from '../default-grid/InternalGrid';
import DefaultDatePicker from '../default-date-picker/DefaultDatePicker';
import SideSheetInput from '../side-sheet/side-sheet-input/SideSheetInput';

export enum ObjectTagValuesMode {
    Values = 0,
    ManualEntry = 1
}

const MAX_STATUS_COUNT_BEFORE_DEVICEMODE_REFRESH = 5;
const LEGACY_CONFIGURATION_ID = -1;

export type ObjectTagValuesProps = {
    visible?: boolean;
    mode: ObjectTagValuesMode;
    grouped: boolean;
    onValueChanged?: (tagValue: ITagValue) => void;
    autoHeight?: boolean;

    fromTest?: boolean; // ONLY for testing!

    setActions?: (items: ActionMenuItem[]) => void;
    resetActions?: () => void;
    actions?: ActionMenuItem[];
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
    noOptionsSideSheet?: boolean;

    canChangeDate?: () => Promise<boolean>;

    selectedDate?: Date;

    variant?: "outlined" | "elevation";
    elevation?: number;


    configurationActions?: ConfigurationActionEnum[];
    setConfigurationActions?: (actions: ConfigurationActionItem[]) => void;

    dense?: boolean;
    dashboardComponent?: IObjectTagValuesDashboardComponent;
};

export type ObjectTagValuesType = {
    executeAction: (action: ConfigurationActionEnum | BaseActionEnum) => void;
    setOptions: (optionsState: ObjectTagValuesColumnOptionsState) => void;
};

function ObjectTagValues({
    visible,
    mode,
    onValueChanged,
    grouped,
    autoHeight,
    fromTest,
    setActions,
    resetActions,
    actions,
    setViewOptionState,
    noOptionsSideSheet,
    canChangeDate,
    selectedDate,
    variant,
    elevation,
    setConfigurationActions,
    dense,
    dashboardComponent
}: ObjectTagValuesProps, ref: Ref<unknown> | undefined) {

    const writeTagsRef = useRef<IEntityValueTagModel[]>([]);
    const [, dispatchAppSettings] = useAppSettings();
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const [,
        getActiveStatus,
        demandScan,
        cancel,
        writeDown
    ] = useLiveDataContext();

    const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.HeaderAndActionsV2,
    });

    const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);


    const collapseTagGroups = userState.userSettings?.collapseTagGroups ?? false;

    const tankLevelGraphRef = useRef<TankLevelGraphType>(undefined);
    const [progressState, actionStarted] = useProgressContext();   
    const [, setAppNotificationState] = useAppNotificationContext();
    const navigateTo = useNavigateTo();
    const theme = useTheme();
    const durationDialog = useCustomDurationDialogState();
    const optionsDialog = useObjectGraphOptionsDialogState();
    const writeDownFormDialog = useWriteDownFormDialogState();
    const configEditorDialog = useObjectTagValuesConfigurationEditorDialogState();
    const defaultConfigurationDialog = useDefaultConfigurationDialogState();
    const renameConfigurationDialog = useRenameConfigurationDialogState();
    const accessLevelDialog = useAccessLevelDialogState();
    const {
        configurationsContextState, 
        setContextConfigurations,
        setContextConfigurationID
    } = useConfigurationsContext();

    const offlineConfig = OfflineDataService.getOfflineConfig();

    const { state,
        setInWriteDownMode,
        setGridApi,
        setData,
        setCommandInProgress,
        setLastDemandScanTimestamp,
        setPreWriteDownBeforeDemandScan,
        setWriteDownTags,
        setCategories,
        setDefaultCategory,
        setView,
        setDuration,
        setShowAlarmSetpointThreshold,
        setShowTagDescriptions,
        resetOptions,
        setGraphOptions,
        setSelectedDate,
        setEndDate,
        setRefreshing,
        resetDurationOptions,
        setDownloadUid,
        setDataError,
        setConfigurationID,
        setDefaultConfigurationID,
        setColumnOptions,
        setForceMinMax,
        setMinMaxY,
        setMinMaxError,
    } = useObjectTagValueState();

    const startDataEntry = useRef(false);

    const { data: objectViewState } = useObjectViewState({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: {
            id: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group
        },
        offline: offlineState.offline
    });

    const configurationAllowed = useMemo(() => {
        return (objectViewState?.accessPermissions?.some(o => o.functionalityType === AccessFunctionalityTypeEnum.ObjectTagValuesConfiguration && 
                            (o.access & AccessTypeEnum.Read_View) > 0) ?? false);
    }, [
        objectViewState?.accessPermissions
    ]);

    const isPhone = isMobile && !isTablet;

    const { confirm, acknowledge } = useConfirmation();
    const reviewWriteDownDialog = useConfirmWriteDownDialogState();

    useImperativeHandle<unknown, ObjectTagValuesType>(
        ref,
        () => ({
            executeAction: executeAction,
            setOptions: (optionsState) => {
                setColumnOptions(optionsState)
            }
        })
    );

    const { data: dataResponse, refetch, status } = useQueryNotifications(useTagValues)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: routeContext.contextObject?.id as number,
        objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
        date: mode === ObjectTagValuesMode.Values ? undefined : state.options.endDate,
        offline: offlineState.offline,
        manualEntry: mode === ObjectTagValuesMode.ManualEntry,
        useLKVOffline: offlineConfig.useLKV,
        configurationID: state.options.configurationID,
        enabled: !state.inWriteDownMode,
        onSuccess: () => {
            setTimeout(() => setRefreshing(false), 2000);
            setDataError(false);
        },
        onError: (error: RestError) => {
            setRefreshing(false);

            setDataError(true);

            const problemDetails = (error.response?.data as unknown) as IApiProblemDetails;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                title: undefined,
                message: t("objectTagValues.dataErrorText"),
                hideContactInfo: false,
                error: !problemDetails?.message ? error : undefined,
                show: true
            });


        }
    });

    const groupID = routeContext.groupID!;
    const contextObject = routeContext.contextObject!

    const {
        data: configurations,
    } = useQueryNotifications(useObjectTagValuesConfigurations)({
        groupID: groupID,
        objectType: contextObject.objectType,
        objectID: contextObject.id,
        enabled: configurationAllowed 
    });

    const { mutate: renameConfiguration } = useQueryNotifications(useRenameObjectTagValuesConfiguration)({
        groupID: groupID,
        objectType: contextObject.objectType,
        objectID: contextObject.id,
        configurationID: state.options.configurationID!,
        onSuccess: (data) => {
            acknowledge(t("configuration.renameSuccessMessage", {
                configurationType: t("configuration.types.objectTagValues"),
                oldName: data.oldName,
                newName: data.newName
            }));
        }
    });

    const { mutate: modifyAccess } = useQueryNotifications(useModifyObjectTagValuesConfigurationAccess)({
        groupID: groupID,
        objectType: contextObject.objectType,
        objectID: contextObject.id,
        configurationID: state.options.configurationID!,
        onSuccess: (data) => {
            acknowledge(t("configuration.accessLevelModifySuccessMessage", {
                configurationType: t("configuration.types.objectTagValues"),
                oldAccess: getAccessText(data.oldAccess),
                newAccess: getAccessText(data.newAccess)
            }));
        }
    });

    const { mutate: deleteConfiguration } = useQueryNotifications(useDeleteObjectTagValuesConfiguration)({
        groupID: groupID,
        configurationID: state.options.configurationID!,
        objectType: contextObject.objectType,
        objectID: contextObject.id,
        onSuccess: (data) => {

            acknowledge(t("configuration.deleteSuccessMessage", {
                configurationType: t("configuration.types.objectTagValues"),
                configurationName: data.deletedName
            }));

            setConfigurationID(configurations?.defaultConfigurationID);
        }
    });

    const { mutate: setDefaultConfiguration } = useQueryNotifications(useSaveObjectTagValuesDefault)({
        groupID: groupID,
        objectType: contextObject.objectType,
        objectID: contextObject.id,
        onSuccess: (data) => {
            setDefaultConfigurationID(data.configurationID);

            let message = "";

            if (data.forCurrentUser && data.forAllUsers) {
                message = t("configuration.saveAsDefaultSuccess1", { objectTypeName: t("configuration.types.objectTagValues") });
            } else if (data.forCurrentUser && data.forUsersWithoutDefault) {
                message = t("configuration.saveAsDefaultSuccess2", { objectTypeName: t("configuration.types.objectTagValues") });
            } else if (data.forAllUsers) {
                message = t("configuration.saveAsDefaultSuccess3", { objectTypeName: t("configuration.types.objectTagValues") });
            } else if (data.forUsersWithoutDefault) {
                message = t("configuration.saveAsDefaultSuccess4", { objectTypeName: t("configuration.types.objectTagValues") });
            } else {
                message = t("configuration.saveAsDefaultSuccess5", { objectTypeName: t("configuration.types.objectTagValues") });
            }

            acknowledge(message);
        }
    });

    const { refetch: getDefaultOptions } = useQueryNotifications(useObjectTagValuesConfigurationDefaultOptions)({
        groupID: groupID,
        configurationID: state.options.configurationID!,
        objectType: contextObject?.objectType !== eLynxObjectTypeEnum.Group
            ? contextObject?.objectType
            : undefined,
        objectID: contextObject?.objectType !== eLynxObjectTypeEnum.Group
            ? contextObject?.id
            : undefined,
        enabled: false
    });

    const getConfigurationDescription = useCallback((
        configurationID: number | undefined
    ): ObjectTagValuesConfigurationDescription | undefined => {
        return configurations?.configurations?.find(o => o.id === configurationID);
    }, [
        configurations?.configurations
    ]);
    
    const sharedConfigurations = useMemo(() => {
        return configurations?.configurations?.filter(o => o.access === AccessType.Shared && !o.internal)
        .map(o => {
            return {
                value: o.id,
                label: o.name,
                isDefault: o.id === state.options.defaultConfigurationID
            } as SideSheetGroupedSelectOption;
        }) ?? [];
    }, [
        configurations?.configurations,
        state.options.defaultConfigurationID
    ]);

    const sharedReadOnlyConfigurations = useMemo(() =>  {
        return configurations?.configurations?.filter(o => o.access === AccessType.SharedReadOnly && !o.internal)
        .map(o => {
            return {
                value: o.id,
                label: o.name,
                isDefault: o.id === state.options.defaultConfigurationID,
                createdUserName: o.createdByUserName ?? 'Unknown'
            } as SideSheetGroupedSelectOption;
        }) ?? [];
    }, [
        configurations?.configurations,
        state.options.defaultConfigurationID
    ]);

    const privateConfigurations = useMemo(() => {
        return configurations?.configurations?.filter(o => o.access === AccessType.Private && !o.internal)
        .map(o => {
            return {
                value: o.id,
                label: o.name,
                isDefault: o.id === state.options.defaultConfigurationID
            } as SideSheetGroupedSelectOption;
        }) ?? [];
    }, [
        configurations?.configurations,
        state.options.defaultConfigurationID
    ]);

    // #endregion Configuration

    const isTank = (routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Device && 
    dataResponse && 
    (dataResponse as IEntityValueDeviceModel).deviceType === DeviceTypesEnum.StandaloneTank) ||
    routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tank;

    const statusCount = useRef(0);
    const navigationObjects = dataResponse ? ObjectTagValueUtils.getNavigationObjects(dataResponse, routeContext.groupID as number) : [];


    const categoryList = useMemo(() => {
        return ObjectTagValueUtils.getViewCategories(dataResponse, state.categories, state.options.view);
    }, [
        dataResponse, 
        state.categories, 
        state.options.view
    ]);

    // const startCellEdit = useCallback(() => {
    //     if (state.gridApi) {

    //         // find first row where tag has write down
    //         let row = mode === ObjectTagValuesMode.Values ?
    //             state.data?.rows.find(o => (((o.tag as IEntityValueTagModel).features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.Writedown) > 0) :
    //             state.data?.rows.find(o => (((o.tag as IEntityValueTagModel).features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.ManualEntry) > 0);
    //         row = state.gridApi?.getRowNode(row?.id);

    //         const rowIndex = row?.rowIndex ?? 1;

    //         if (row) {
    //             state.gridApi.ensureIndexVisible(rowIndex);
    //         }

    //         state.gridApi.stopEditing(true);


    //         state.gridApi.setFocusedCell(rowIndex, 'value');

    //         state.gridApi.startEditingCell({
    //             rowIndex: rowIndex,
    //             colKey: 'value'
    //         });
    //     }
    // }, [state.gridApi,
    // state.data?.rows,
    //     mode
    // ]);


    const _onDataChanged = useCallback((tag: IEntityValueTagModel, 
        newValue: string, writeDown: boolean) => {

        const textValue: string | undefined = (tag.dataType === DataTypeEnum.Double || 
                                            (newValue.trim() === '' && mode === ObjectTagValuesMode.ManualEntry)) ? 
                                            undefined : 
                                            newValue;

        let value: number | undefined = newValue.trim() === '' && mode === ObjectTagValuesMode.ManualEntry ? 
                            undefined : 
                            +((newValue as string).replace(/,/g, ''));

        //console.log(`_onDataChanged value: ${value}  tagValue: ${tag.tagValue?.value}`);
        let displayText: string = newValue;

        const rowNode = state.gridApi?.getRowNode(`${tag.categoryID ?? UNGROUPED_CATEGORY_ID}:${tag.id}`);
        // if (rowNode) {
        //     const rowData = { ...rowNode.data };
        //     rowData.error = false;
        //     rowNode.setData(rowData);            
        // }

        if (tag?.listID) {
            const listText = tag?.lookupList?.listText.find(o => o.description === newValue || o.value === newValue);
            if (listText) {
                value = +(listText.value as string);
                displayText = listText.description;
            }
        } else if (((tag?.features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.FeetAndInches) > 0) {

            let total = getTotalFromFeetAndInchesText(newValue);

            if (total && tag.unitDisplayName?.toLowerCase() === 'feet') {
                total /= 12;
            }

            value = total as number;
            displayText = newValue;
        } else if (((tag?.features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.HoursMinutesSeconds) > 0) {
                const parts = newValue.split(':');
                let total = 0;
                if (parts.length === 3) {
                    total = +parts[0] * 3600 + +parts[1] * 60 + +parts[2];
                } else if (parts.length === 2) {
                    total = +parts[0] * 60 + +parts[1];
                } else {
                    total = +parts[0];
                }
    
                value = total as number;
                //displayText = newValue;
                displayText = getHoursMinutesSecondsText(value);
        } else if (tag.dataType === DataTypeEnum.String) {
            value = undefined;
        } else {
            if (newValue !== '' && !writeDown) {
                displayText = Number(newValue.replace(/,/g, '')).toFixed(tag.decimalPlaces ?? 2);
            }

            if (value !== undefined && isNaN(value)) {
                value = undefined;
            }

            if (mode === ObjectTagValuesMode.ManualEntry) {

                // same value, nothing to do
                if (tag.tagValue?.value !== undefined && tag.tagValue.value === value) {
                    return;
                }

                // can only delete manual entry values
                if (tag.tagValue?.value !== undefined && tag.tagValue?.setPointEventType !== SetPointEventTypeEnum.Manual && value === undefined) {

                    //console.log('resetting manual entry value!')
                    value = tag.tagValue?.value;
                    newValue = tag.tagValue?.displayText as string;

                    //const rowNode = state.gridApi?.getRowNode(`${tag.categoryID ?? UNGROUPED_CATEGORY_ID}:${tag.id}`);
                    if (rowNode) {
                        rowNode.setDataValue('value', tag.originalTagValue ? tag.originalTagValue?.displayText : '');
                    }

                    return;
                }
            }
        }

        let timestampString = '';
        if (state.options.endDate) {

            //console.log(`dataChanged date: ${JSON.stringify(state.options.endDate)}`);

            
            const copy = new Date(state.options.endDate.getFullYear(),
                        state.options.endDate.getMonth(),
                        state.options.endDate.getDate()
                        );
            timestampString = copy.toISOString();
        }

        const newTagValue: ITagValue = tag.originalTagValue?.value !== undefined ? {
            ...tag.tagValue,
            value: value,
            textValue: textValue,
            displayText: displayText,
            setPointEventType: SetPointEventTypeEnum.Manual,
            timestamp: timestampString                
        } : {
            value: value,
            textValue: textValue,
            displayText: displayText,
            setPointEventType: SetPointEventTypeEnum.Manual,
            tagID: tag.id,
            unitOfMeasureID: tag.uomID,
            unitDisplayName: tag.unitDisplayName,
            timestamp: timestampString,
            dataType: tag.dataType
        };

        tag.tagValue = newTagValue;
        tag.selected = true;

        let tags = state.writeDownTags.filter(o => o.id !== tag.id);
        tags.push(tag);
        tags = tags.sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));
        setWriteDownTags(tags); 
        writeTagsRef.current = tags; 

        if (onValueChanged) {
            onValueChanged(newTagValue);
        }


            let row = state.data?.rows.find(o => (((o.tag as IEntityValueTagModel).id === tag.id)));
            if (row) {
                row = state.gridApi?.getRowNode(row?.id);

                const rowIndex = (row?.rowIndex ?? 1) + 1;

                setTimeout(() => {
                    state.gridApi?.setFocusedCell(rowIndex, 'value');

                    if (!isPhone) {
                        state.gridApi?.startEditingCell({
                            rowIndex: rowIndex,
                            colKey: 'value'
                        });
                    }
                }, 250);
                
            }

        return tag;
    }, [
        state.options.endDate,
        setWriteDownTags,
        onValueChanged,
        mode,
        state.gridApi,
        state.writeDownTags,
        state.data,
        isPhone
    ]);

    const resetWriteDownValues = useCallback((tags: IEntityValueTagModel[]) => {

        tags.forEach((tag) => {
            tag.selected = false;

            const rowNode = state.gridApi?.getRowNode(`${tag.categoryID ?? UNGROUPED_CATEGORY_ID}:${tag.id}`);
            if (rowNode) {

                rowNode.setDataValue('value', tag.originalTagValue ? tag.originalTagValue?.displayText : '...');

                if (!isPhone) {
                    rowNode.setDataValue('timestamp', tag.originalTagValue ?
                        getTimeframeTimeDisplayText(tag.originalTagValue?.displayTimeframe as TimeframeTypeEnum,
                            new Date(tag.originalTagValue?.timestamp as string)) :
                        '...');
                }
            }
        });

    }, [state.gridApi,
        isPhone]);


    const updateLiveDataStatus = useCallback((status: CommandStatusEnum, rows: RowDef[]) => {
    

        rows.forEach((row) => {

            const rowNode = state.gridApi?.getRowNode(`${row.tag.categoryID ?? UNGROUPED_CATEGORY_ID}:${row.tag.id}`);
            if (rowNode) {
                const rowData = { ...rowNode.data };
                rowData.liveDataStatus = status;
                rowNode.setData(rowData);
            }
        });

    }, [state.gridApi]);

    const updateTagInError = useCallback((inError: boolean, rows: RowDef[]) => {
    

        rows.forEach((row) => {

            const rowNode = state.gridApi?.getRowNode(`${row.tag.categoryID ?? UNGROUPED_CATEGORY_ID}:${row.tag.id}`);
            if (rowNode) {
                const rowData = { ...rowNode.data };
                rowData.error = inError;
                rowNode.setData(rowData);
            }
        });

    }, [state.gridApi]);


    const updateWriteDownModeRows = useCallback((writeDownMode: boolean) => {
    
        const rowNodes = state.data?.rows.filter(o => (((o.tag as IEntityValueTagModel).features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.Writedown) > 0);
        rowNodes?.forEach((rowNode) => {
            const gridRowNode = state.gridApi?.getRowNode(rowNode?.id);
            if (gridRowNode) {
                const rowData = { ...gridRowNode.data };
                rowData.inWriteDownMode = writeDownMode;
                gridRowNode.setData(rowData);
            }
        });

    }, [state.data?.rows,
    state.gridApi]);
    

    const exitWriteDownMode = useCallback(() => {
    
        state.gridApi?.stopEditing();
        setInWriteDownMode(false);

        resetWriteDownValues(state.writeDownTags);

        updateWriteDownModeRows(false);

        setWriteDownTags([]);

        setData({
            cols: [],
            rows: []
        });

        setTimeout(() => {
            state.gridApi?.redrawRows();
        }, 0);

        resetActions?.();

    }, [state.gridApi,
        state.writeDownTags,
        setInWriteDownMode,
        resetWriteDownValues,
        updateWriteDownModeRows,
        setWriteDownTags,
        resetActions,
        setData
    ]);


     const onRefreshData = useCallback((forceUpdate?: boolean) => {
    
        if ((!state.commandInProgress || forceUpdate) && state.data) {

            setRefreshing(true);

            const rows = state.data.rows.filter(o => ObjectTagValueUtils.hasLiveData(o.tag));

            updateLiveDataStatus(CommandStatusEnum.None, rows);
            // setData({
            //     rows: [],
            //     cols: []
            // });

             if (state.options.durationOptions.option !== DurationOptionEnum.Custom) {

                let dayOffset = -1;

                switch (state.options.durationOptions.option) {
                    //case DurationOptionEnum.OneDay:
                    //    break;

                    case DurationOptionEnum.ThreeDays:
                        dayOffset = -3;
                        break;

                    case DurationOptionEnum.SevenDays:
                        dayOffset = -7;
                        break;

                    case DurationOptionEnum.ThirtyDays:
                        dayOffset = -30;
                        break;

                    default:
                        break;
                }

                const endDate = new Date();
                const startDate = addDays(endDate, dayOffset);

                setDuration({...state.options.durationOptions, 
                    duration: {
                        startDate: startDate,
                        endDate: endDate

                    }});

             }

            refetch();
        }
    }, [
        state.commandInProgress,
        state.data,
        updateLiveDataStatus,
        refetch,
        setRefreshing,
        setDuration,
        state.options.durationOptions
        //setData
    ]);
    

    const onLiveDataCommand = useCallback(async (command: BaseActionEnum, preWriteDown?: boolean, tags?: IEntityValueTagModel[]) => {
        try {
            statusCount.current = 0;

            setCommandInProgress(true);

            let rows: RowDef[] | undefined;

            switch (command) {
                case BaseActionEnum.DemandScan:

                    rows = state.data?.rows.filter(o => preWriteDown ? ObjectTagValueUtils.hasWriteDown(o.tag) : ObjectTagValueUtils.hasLiveData(o.tag));

                    await demandScan(routeContext.contextObject as IeLynxObject, preWriteDown);

                    actions?.forEach(o => o.disabled = true);
                    actions?.flatMap(o => o.subActions ?? []).forEach(o => o.disabled = true);
                    setActions?.(actions ?? []);

                    break;

                case BaseActionEnum.SubmitWriteDown: {

                    const values = tags?.map(o => o.tagValue as ITagValue);

                    if (values) {

                        rows = values.reduce((result: RowDef[], currentValue) => {
                            return result.concat((state.data as GridData).rows.filter(o => o.tag.id === currentValue.tagID));
                        }, []);

                        writeDown(routeContext.contextObject as IeLynxObject, values);
                    }

                    updateWriteDownModeRows(false);

                    state.gridApi?.stopEditing();
                    state.gridApi?.deselectAll();

                    break;
                }
            }

            updateLiveDataStatus(CommandStatusEnum.InProgress, rows as RowDef[]);

            const colDef = state.gridApi?.getColumnDef('value');
            if (colDef) {
                colDef.enableCellChangeFlash = true;
            }


        } catch (error) {
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                error: error as RestError,
                show: true
            });

            setCommandInProgress(false);
        }
    }, [
        setCommandInProgress,
        updateLiveDataStatus,
        state.gridApi,
        setAppNotificationState,
        updateWriteDownModeRows,
        demandScan,
        writeDown,
        actions,
        setActions,
        state.data,
        routeContext.contextObject,
    ]);

    // NOTE: This is a hack to get around some of the issues with the ag-grid cell editing introduced in the
    // latest update.  This will be removed once the issues are resolved.
    // Bug 52109: Live Data Cell Editing not working with decimals - DOC - ZD: 235759
    async function _onSubmitWriteDownDelayed() {

        state.gridApi?.stopEditing();
    
        setTimeout(() => {
            onSubmitWriteDown();
        }, 500);
    }

    const onSubmitWriteDown = useCallback(async () => {

        state.gridApi?.stopEditing();

        if (dataResponse?.objectType === eLynxObjectTypeEnum.Device) {

            const validateGasAnalysisInput = (values: IEntityValueTagModel[], tolerance: number): boolean => {
        
                // sum all gas analysis tag values, the total must not be more than 100% +- tolerance.
                const totalPercent: number = Enumerable.from(values).sum(o => o.tagValue?.value ?? 0);

                const maxDecimals = Enumerable.from(values).max(o => o.decimalPlaces ?? 0);
                const roundedValue = _.round(Math.abs(100 - totalPercent), maxDecimals);
                return roundedValue > tolerance;
            }

            const device = dataResponse as IEntityValueDeviceModel;

            const gasAnalysisTags = _.cloneDeep(device?.tags.filter(o => o.features && (o.features & ObjectFeaturesEnum.GasComposition) > 0) ?? []);

            if (gasAnalysisTags) {

                writeTagsRef.current.forEach(o => {
                    const tag = gasAnalysisTags.find(v => v.id === o.id);
                    if (tag) {
                        tag.tagValue = o.tagValue;
                    }
                });

                const gridRows = state.data!.rows.filter(o => o.tag.features && (o.tag.features & ObjectFeaturesEnum.GasComposition) > 0);

                if (writeTagsRef.current?.some(o => ((o.features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.GasComposition) > 0))
                {

                    if (validateGasAnalysisInput(gasAnalysisTags, device.gasAnalysisTolerance!)) {

                        updateTagInError(true, gridRows);

                        setAppNotificationState({
                            notificationType: AppNotificationTypeEnum.Warning,
                            message: `The sum of all gas composition tags for this device must equal 100% +-(${device.gasAnalysisTolerance}).`,
                            show: true,
                            hideContactInfo: true
                        });
            

                        return;
                    } else {
                        updateTagInError(false, gridRows);
                    }
                }
            }
        }



        // NOTE: This is a hack to get around some of the issues with the ag-grid cell editing introduced in the
        // latest update.  This will be removed once the issues are resolved.
        // Bug 52109: Live Data Cell Editing not working with decimals - DOC - ZD: 235759
        // because of the weird lifecycle events in ag-grid, we need the writedown values from the ref since
        // the state may or may not be updated yet
        //const result = await reviewWriteDownDialog.open(state.writeDownTags);
        const result = await reviewWriteDownDialog.open(writeTagsRef.current);
        
        if (result.reason === "ok") {
            onLiveDataCommand(BaseActionEnum.SubmitWriteDown, false, result.values as IEntityValueTagModel[]);
        } else {
            exitWriteDownMode();
        }
    }, [
        reviewWriteDownDialog,
        //state.writeDownTags,
        onLiveDataCommand,
        exitWriteDownMode,
        state.gridApi,
        dataResponse,
        state.data,
        updateTagInError,
        setAppNotificationState
    ]);
    

    const writeDownInForm = useCallback(async (): Promise<void> => {

        const result = await writeDownFormDialog.open({
            objectName: dataResponse?.displayText ?? '',
            writedownMode: true,
            configurationID: state.options.configurationID,
        });

        if (result.reason === 'cancel') {
            exitWriteDownMode();
        } else {

            if (result.values?.newValues) {

                try {

                    // we need to get all of the tags because the user can select to use all tags overriding the configuration
                    // in the write down form dialog

                    const model = await TagValueDataService.getTagValueData(
                                userState.customer!.id, 
                                routeContext.groupID!, 
                                routeContext.contextObject!, 
                                undefined, 
                                false,
                                offlineConfig.useLKV,
                                -1
                    );


                    const writeTags: IEntityValueTagModel[] = [];
                    result.values?.newValues.forEach(o => {
                        const writeTag = model?.tags.find(v => v.id === o.tagID);
                        if (writeTag) {

                            writeTag.originalTagValue = writeTag.tagValue;
                            let newValue = o.value?.toString() ?? '';
                            if (o.value !== undefined && writeTag.features && (writeTag.features & ObjectFeaturesEnum.HoursMinutesSeconds) > 0) {
                                newValue = getHoursMinutesSecondsText(+o.value);
                            }

                            else if (o.value !== undefined && writeTag.features && (writeTag.features & ObjectFeaturesEnum.FeetAndInches) > 0) {

                                //newValue = getFeetAndInchesText(o.value);
                                newValue = o.displayText ?? '';
                            }

                            const updatedWriteTag = _onDataChanged(writeTag, newValue, true);
                            if (updatedWriteTag) {
                                writeTags.push(updatedWriteTag);
                            }
                        }
                    });

                    //await onSubmitWriteDown();
                    const reviewResult = await reviewWriteDownDialog.open(writeTags);

                    if (reviewResult.reason === "ok") {
                        onLiveDataCommand(BaseActionEnum.SubmitWriteDown, false, reviewResult.values as IEntityValueTagModel[]);
                    } else {
                        exitWriteDownMode();
                    }
                } catch (error) {
                    const restError = error as RestError;
                    
                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        show: true,
                        error: restError
                    });
                }
            } else {
                exitWriteDownMode();
            }
        }
    }, [
        reviewWriteDownDialog,
        writeDownFormDialog,
        dataResponse,
        state.options.configurationID,
        userState.customer,
        routeContext.groupID,
        routeContext.contextObject,
        offlineConfig.useLKV,
        _onDataChanged,
        onLiveDataCommand,
        exitWriteDownMode,
        setAppNotificationState,
    ]);


    const enableWriteDownMode = useCallback((): void => {

        updateLiveDataStatus(CommandStatusEnum.None, state.data?.rows as RowDef[]);

        // clear the list of update write down tags
        setWriteDownTags([]);

        setInWriteDownMode(true);

        actions?.forEach(o => o.disabled = true);
        actions?.flatMap(o => o.subActions ?? []).forEach(o => o.disabled = true);
        setActions?.(actions ?? []);

        updateWriteDownModeRows(true);


        //if (isPhone) {
            writeDownInForm();
        //}

        //start editing the first cell
        //startCellEdit();

    }, [setInWriteDownMode,
        //startCellEdit,
        state.data,
        updateLiveDataStatus,
        setWriteDownTags,
        updateWriteDownModeRows,
        actions,
        setActions,
        //isPhone,
        writeDownInForm
    ]);


    const doLiveData = useCallback((item: LiveDataStateItem) => {
    //const doLiveData = (item: LiveDataStateItem) => {        

        if (state.commandInProgress) {

            let newStatusCount = statusCount.current + 1;

            // if the current device is configured with control modes (device specs) then reload the data to pick up
            // the new configuration and values.  NOTE: this will be limited to every MAX_STATUS_COUNT_BEFORE_DEVICEMODE_REFRESH to keep from
            // making excessive calls to reload the data
            if (dataResponse?.objectType === eLynxObjectTypeEnum.Device &&
                ((dataResponse?.features ?? 0) & ObjectFeaturesEnum.DeviceSpec) > 1 &&
                (newStatusCount > MAX_STATUS_COUNT_BEFORE_DEVICEMODE_REFRESH ||
                    item.commandComplete)) {

                // reset the status counter
                newStatusCount = 0;

                sleep(3000).then(() => onRefreshData());
            }

            statusCount.current = newStatusCount;


            item.status?.deviceStatusList.forEach(o => {
                o.statusItems.forEach(v => {
                    const tag = dataResponse?.tags.find(o => o.id === v.tagID);

                    const row = state.gridApi?.getRowNode(`${tag?.categoryID ?? UNGROUPED_CATEGORY_ID}:${tag?.id}`);

                    if (row) {

                        const rowData = { ...row.data };
                        if (v.status !== CommandStatusEnum.None) {
                            rowData.liveDataStatus = v.status;
                        }

                        if (v.valueValid && v.value) {

                            const tag = row.data.tag as ITagModel;

                            const timestamp = v.value.timestamp as string;

                            if (tag && ((tag.tagValue &&
                                new Date(timestamp).valueOf() >
                                (new Date(tag.tagValue?.timestamp as string).valueOf() ?? 0))
                                || (!tag.tagValue))) {

                                const source = dataResponse?.tags.find(o => o.id === tag.id);
                                if (source) {
                                    source.tagValue = v.value;
                                    source.originalTagValue = v.value;
                                }

                                rowData.value = v.value.displayText;

                                if (!isPhone) {
                                    rowData.timestamp = getTimeframeTimeDisplayText(v.value.displayTimeframe as TimeframeTypeEnum, new Date(timestamp));
                                }

                                // state.gridApi?.flashCells({
                                //     rowNodes: [row],
                                //     columns: !isPhone ? ['value', 'timestamp'] : ['value']
                                // });
                            }
                        }

                        row.setData(rowData);
                        state.gridApi?.redrawRows({
                            rowNodes: [row]
                        });
                    }
                });
            });

            if (item.commandComplete) {

                const colDef = state.gridApi?.getColumnDef('value');
                if (colDef) {
                    colDef.enableCellChangeFlash = false;
                }

                if (item.commandState === LiveDataCommandStateEnum.DemandScanComplete) {
                    setLastDemandScanTimestamp(new Date());
                }

                if (item.status?.command === LiveDataCommandEnum.Write) {
                    exitWriteDownMode();
                }

                setCommandInProgress(false);

                if (state.preWriteDownDemandScan) {
                    setPreWriteDownBeforeDemandScan(false);
                    enableWriteDownMode();
                } else {
                    resetActions?.();
                }


                //sleep(3000).then(() => onRefreshData(true));

                window.setTimeout(() => {
                    //console.log('object tag values refresh from live data')
                    refetch();
                }, 10000);

            }
        }

    }, [
        isPhone,
        dataResponse,
        onRefreshData,
        setLastDemandScanTimestamp,
        state.commandInProgress,
        setCommandInProgress,
        state.gridApi,
        exitWriteDownMode,
        state.preWriteDownDemandScan,
        setPreWriteDownBeforeDemandScan,
        enableWriteDownMode,
        resetActions,
        refetch,
    ]);


    // NOTE: I don't think this can be moved to useTagValues query since we need to control
    // it based on the mode and command in progress
    // AutoRefresh support only for tag values, not for manual entry
    useAutoRefresh({
        dataRefresh: visible &&
            mode === ObjectTagValuesMode.Values &&
            !state.inWriteDownMode &&
            !state.commandInProgress ?
            () => {
                onRefreshData();
            } :
            () => {/*console.log nothing */ },
        category: UIRefreshCategoryEnum.Details
    });

    function onGridReady(event: GridReadyEvent) {
        //console.log('onGridReady');
        setGridApi(event.api);
    }

    // function onFirstDataRendered(event: FirstDataRenderedEvent) {

    //     if (mode === ObjectTagValuesMode.ManualEntry) {
    //         //console.log('onFirstDataRendered')
    //         //2022-09-16 Per Chris don't select first editable cell
    //         //startCellEdit();
    //     }
    // }

    // function onComponentStateChanged(event: ComponentStateChangedEvent) {
    //     console.log('onComponentStateChanged')
    //     if (event.api.getRenderedNodes().length) {
    //         startCellEdit();
    //     }
    // }
    // function onNewColumnsLoaded(event: NewColumnsLoadedEvent) {

    //     console.log('onNewColumnsLoaded')
    // }
    // /** The list of grid columns changed. */
    // function onGridColumnsChanged(event: GridColumnsChangedEvent) {
    //     console.log('onGridColumnsChanged')
    // }


    async function confirmDemandScanBeforeWriteDown() {

        return (await confirm({
            dialogTitle: t("livedata.makeChanges"),
            confirmationText: t("livedata.confirmWriteDown.text"),
            yesButtonText: t("livedata.confirmWriteDown.yes"),
            noButtonText: t("livedata.confirmWriteDown.no"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        }));
    }

    const onRowClicked = (event: RowClickedEvent): void => {

        if (mode === ObjectTagValuesMode.Values && !state.inWriteDownMode) {

            let options: NavigateToOptions | undefined;

            const mouseEvent = event.event as MouseEvent;
            if (mouseEvent && (mouseEvent.shiftKey || mouseEvent.ctrlKey)) {
                options = {
                    newViewType: mouseEvent.shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
                }
            }
            
            if (!event.node.group) {
                if (event.data?.href) {
                    navigateTo(event.data.href, options);
                }
            } else {
                const href = navigationObjects?.find(o => o.Key === event.node.key)?.Value;
                if (href) {
                    navigateTo(href, options);
                } else {
                    state.gridApi?.setRowNodeExpanded(event.node, !event.node.expanded);
                }
            }
        }
    };

    const onDesktopRowClicked = (event: RowClickedEvent): void => {

        if (mode === ObjectTagValuesMode.Values && !state.inWriteDownMode) {
            
            if (event.node.group) {
                state.gridApi?.setRowNodeExpanded(event.node, !event.node.expanded);
            }
        }
    };

    const onEditingStopped = (event: CellEditingStoppedEvent) => {
        const tag: IEntityValueTagModel | undefined = event.data?.tag;
        const valid = event.data?.valid;

        if (!valid && ((tag?.writeLowerBound !== undefined && tag?.writeLowerBound !== null) || 
                        (tag?.writeUpperBound !== undefined && tag?.writeUpperBound !== null))) {

            let text = '';

            const writeLowerBound = tag?.writeLowerBound ?? undefined;
            const writeUpperBound = tag?.writeUpperBound ?? undefined;
            const writeLowerBoundOp = tag?.writeLowerBoundOp === '' ? undefined : tag?.writeLowerBoundOp ?? undefined;
            const writeUpperBoundOp = tag?.writeUpperBoundOp === '' ? undefined : tag?.writeUpperBoundOp ?? undefined;


            if (tag.features && (tag.features & ObjectFeaturesEnum.FeetAndInches) > 0) {
                text = `The value for ${tag?.displayText} is not valid.  `;
            }

            if (writeLowerBound !== undefined && tag?.writeUpperBound !== undefined) {
                if (text.length === 0) {
                    text = `The value for ${tag?.displayText} must be ${writeLowerBoundOp} ${writeLowerBound} and ${writeUpperBoundOp} ${writeUpperBound}.`;
                } else {
                    text += `The value must be ${writeLowerBoundOp} ${writeLowerBound} and ${writeUpperBoundOp} ${writeUpperBound}.`;
                }

            } else {
                if (text.length === 0) {
                    text = `The value for ${tag?.displayText} must be ${writeLowerBoundOp ?? writeUpperBoundOp} ${writeLowerBound ?? writeUpperBound}.`;
                } else {
                    text += `The value must be ${writeLowerBoundOp ?? writeUpperBoundOp} ${writeLowerBound ?? writeUpperBound}.`;
                }
                
            }

            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Warning,
                message: text,
                show: true,
                hideContactInfo: true
            });
        } else if (tag && event.oldValue !== event.newValue) {
            _onDataChanged(tag, event.newValue as string, false);
        }
    };

    

    // const onCellKeyPress = (event: CellKeyPressEvent ) => {
    //     const tag = event.data?.tag as IEntityValueTagModel;

    //     console.log(`keypress value: ${(event.event?.target as HTMLInputElement).value}`);

    //     //const newValue = (event.event?.target as HTMLInputElement)?.value ?? '';
    //     const newValue = (event.event?.target as HTMLInputElement)?.value ?? null;

    //     _onDataChanged(tag, newValue)
    // }

    // const onCellKeyDown = (event: CellKeyDownEvent ) => {

    //     const tag = event.data?.tag as IEntityValueTagModel;
    //     const eventTarget = event.event?.target as HTMLInputElement;

    //     if (eventTarget) {
    //         setTimeout(() => {
            
    //             const newValue = (eventTarget as HTMLInputElement)?.value ?? '';
                
    //             //console.log(`keypress value: ${newValue}`);

    //             if (newValue !== '' && isNaN(+newValue)) {
    //                 return;
    //             }

    //             //const newValue = (event.event?.target as HTMLInputElement)?.value ?? '';
    //             _onDataChanged(tag, newValue);
    //         }, 250);
    //     }
    // }

    const onCellEditingStarted = (event: CellEditingStartedEvent) => {
        
        const tag = event.data?.tag as IEntityValueTagModel;

        // on phones the popup editor position needs to be adjusted because ag grid does not account for the smaller
        // width
        if (isPhone) {


            //console.log(`cell editing started: ${tag.displayText} rowIndex: ${event.rowIndex}`);

            if (tag.features && (tag.features & ObjectFeaturesEnum.FeetAndInches) > 0) {

                const editorContainer = document.querySelector('.ag-popup-editor') as HTMLElement | undefined;
                if (editorContainer) {
                    editorContainer.style.left = '0';
    
    
                    const viewContainer = document.querySelector('#tagvalue-container');
                    if (viewContainer) {
                        const viewContainerHeight = viewContainer.clientHeight + viewContainer.scrollTop;
    
                        const value = editorContainer.offsetTop + editorContainer.clientHeight;
                        if (value > viewContainerHeight) {
                            const currentTop = parseFloat(editorContainer.style.top);
                            const adjustedTop = currentTop  - (value - viewContainerHeight)
                            editorContainer.style.top = `${adjustedTop}px`;
                        }
                    }
                }

            } 
            // else if (!tag.listID) {
            //     //state.gridApi?.setFocusedCell(event.rowIndex as number, 'value');

            //     state.gridApi?.startEditingCell({
            //         rowIndex: event.rowIndex as number,
            //         colKey: 'value'
            //     });
        
            // }

            setTimeout(() => {

                //state.gridApi?.setFocusedCell(event.rowIndex as number, 'value');

                state.gridApi?.startEditingCell({
                    rowIndex: event.rowIndex as number,
                    colKey: 'value'
                });


            }, 0)
        } 
    }

    const onDataChanged = (event: CellValueChangedEvent) => {

        //event.rowIndex
        const tag = event.data?.tag as IEntityValueTagModel;
        //console.log(`changed from ${event.oldValue} to ${event.newValue}`);
        _onDataChanged(tag, event.newValue as string, false);

    };

    // const onGetRowHeight = (params: RowHeightParams): number | undefined => {

    //     if (!isPhone && (mode === ObjectTagValuesMode.Values || !offlineState.offline)) {
    //         return undefined;
    //     }

    //     return ObjectTagValueUtils.getRowHeight(params, theme);
    // };


    async function onExport(action: BaseActionEnum | BaseActionEnum) {

        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.contextObject!.id,
            objectType: routeContext.contextObject!.objectType,
            title: 'Tag Values', 
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        const mimeType = action === BaseActionEnum.Excel ? 
                                ApplicationMimeTypesEnum.MSExcel2007 : 
                                ApplicationMimeTypesEnum.PDF;

        try {

            //const columnState = state.columnApi?.getColumnState();
            //const sortItem = columnState?.find(o => !!o.sort);

            const sortParams: ISortParameters = {
                propertyName: 'name',
                sortAscending: true
            };

            await TagValueDataService.exportTagValues(uid, routeContext.groupID!,
                routeContext.contextObject!, 
                mimeType, 
                sortParams,
                getOptionalColumns());
            
        } catch(error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    }

    // This will maintain the order of the columns as displayed
    function getOptionalColumns(): Array<string> {
        const cols = Enumerable
        .from<ColDef>(state.data?.cols??[])
        .join(
            [
                "uniqueIdentifiers",
                "readItem",
                "writeItem",
                "liveData",
                "listName",
                "formula"
            ],
            o => o.field,
            i => i,
            (_, i) => i
        )
        .toArray();
    
        return cols;
    }

    function executeAction(action: BaseActionEnum | ConfigurationActionEnum | BaseActionEnum) {

        switch (action) {
            case BaseActionEnum.Refresh:
                if (!state.commandInProgress) {

                    setRefreshing(true);

                    if (mode === ObjectTagValuesMode.ManualEntry) {
                        state.gridApi?.undoCellEditing();
                    }

                    refetch();

                    tankLevelGraphRef.current?.executeAction(action);
                }
                break;

            case BaseActionEnum.DemandScan:
                onLiveDataCommand(action);
                break;

            case BaseActionEnum.WriteDown:
                onWriteDown();
                break;

            case BaseActionEnum.Excel:
            case BaseActionEnum.PDF:
                onExport(action);
                break;

            case ConfigurationActionEnum.CreateNew:
                createView();
                break;

            case ConfigurationActionEnum.Edit:
                editView()
                break;

            case ConfigurationActionEnum.ModifyPermissions:
                modifyViewPermissions();
                break;

            case ConfigurationActionEnum.SetAsDefault:
                setDefaultView();
                break;

            case ConfigurationActionEnum.Duplicate:
                duplicateView();
                break;

            case ConfigurationActionEnum.Rename:
                renameView();
                break;

            case ConfigurationActionEnum.Delete:
                deleteView();
                break;
        }
    }

    async function onWriteDown() {

        let demandScanBeforeWrite = ObjectTagValueUtils.shouldDemandScanBeforeWriteDown(dataResponse as IEntityValueDeviceModel | IEntityValueTankModel, state.lastDemandScanTimestamp);

        if (demandScanBeforeWrite) {
            const result = await confirmDemandScanBeforeWriteDown();

            if (result === 'close') {
                return;
            }

            demandScanBeforeWrite = result === 'no';
        }

        if (demandScanBeforeWrite) {
            // set a flag so we know to go into write mode when finished
            setPreWriteDownBeforeDemandScan(true);
            await onLiveDataCommand(BaseActionEnum.DemandScan, true);
        } else {
            enableWriteDownMode();
        }
    }

    const onCancelLiveDataCommand = async () => {

        try {
            if (state.commandInProgress) {
                cancel(routeContext.contextObject as IeLynxObject);
            }
        } catch (error) {
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                error: error as RestError,
                show: true
            });

            setCommandInProgress(false);
        }
    };


    function onCategoryChanged(
        event: React.SyntheticEvent,
        value: ITagCategory[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<ITagCategory> | undefined
    ) {
        selectCategory(reason, details);
    }

    function selectCategory(
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<ITagCategory> | undefined
    ) {


        const categories = [...state.categories];

        let item = categories.find(o => o.id === details?.option.id);

        if (item) {

            switch (reason) {
                case 'selectOption':
                    item.selected = true;

                    if (item.id === ALL_TAGS_CATEGORY_ID || item.id === SUMMARY_LISTING_TAGS_CATEGORY_ID) {
                        categories.forEach(o => {
                            if (o.id !== item?.id) {
                                o.selected = false;
                            }
                        });
                    } else {

                        categories.filter(o => o.id === ALL_TAGS_CATEGORY_ID || o.id === SUMMARY_LISTING_TAGS_CATEGORY_ID)
                            .forEach(o => o.selected = false);
                    }

                    break;

                case 'removeOption':
                    item.selected = false;

                    if (!categories.find(o => o.selected)) {
                        item = categories.find(o => o.id === state.defaultCategory?.id);
                        if (item) {
                            item.selected = true;
                        }
                    }
                    break;

                default:
                    break;
            }
        }

        const liveDataStateItem = getActiveStatus(routeContext.contextObject as IeLynxObject);

        let defaultConfigurationTATs: number[] = [];

        if (objectViewState) {
            defaultConfigurationTATs = (objectViewState as IGroupViewState).defaultConfigurationTATs;
        }

        const rows: Array<RowDef> = ObjectTagValueUtils.createGridRows(dataResponse,
            routeContext,
            mode ?? ObjectTagValuesMode.Values,
            offlineState.offline,
            liveDataStateItem,
            categories.filter(o => o.selected),
            defaultConfigurationTATs,
            isPhone,
            state.inWriteDownMode,
            userState.userSettings?.tankLevelInches ?? false
        );


        setData({
            cols: state.data?.cols as ColDef[],
            rows: rows
        });

        setCategories(categories);
    }

    function onViewChanged(
        event: ChangeEvent<HTMLInputElement>,
    ) {
        const value = Number(event.target.value) as ObjectTagValuesViewEnum;
        const isGraph = value === ObjectTagValuesViewEnum.Chart;
        actions?.filter(o => o.action === BaseActionEnum.DemandScan || o.action === BaseActionEnum.WriteDown)
            ?.forEach(o => o.disabled = isGraph || state.commandInProgress);
        setActions?.(actions ?? []);
        setView(value);
        if (isGraph) {
            // If moving to chart mode and there are only tag/tat groups selected that
            // do not show up in chart view, then select the All Tags category
            const viewCategories = ObjectTagValueUtils.getViewCategories(dataResponse, state.categories, value);
            if (viewCategories.filter(o => o.selected).length === 0) {
                const defaultCategoryID = isPhone && (userState.userSettings?.defaultDeviceView ?? false) ?
                    SUMMARY_LISTING_TAGS_CATEGORY_ID :
                    ALL_TAGS_CATEGORY_ID;

                selectCategory('selectOption', {
                    option: state.categories.find(o => o.id === defaultCategoryID)
                } as AutocompleteChangeDetails<ITagCategory>);
            }
        }
    }

    function isCellEditable(params: EditableCallbackParams): boolean {

        let isEditable = false;

        if (params.colDef.field === 'value') {
            const tag = params.data.tag as IEntityValueTagModel;

            if (mode === ObjectTagValuesMode.ManualEntry) {

                isEditable = ((tag.features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.ManualEntry) > 0;
            }
            else {
                isEditable = ((tag.features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.Writedown) > 0;
            }
        }

        return isEditable;
    }

    async function onCustomDuration() {
        const result = await durationDialog.open(state.options.durationOptions);

        if (result.reason === "ok") {
            const values = result.values as CustomDurationDialogValues;

            setDuration(values);
        }
    }


    const onOverrideSettings = async () => {

        const result = await optionsDialog.open({
            groupID: routeContext.groupID!,
            contextObject: routeContext.contextObject!,
            contextObjectType: routeContext.contextObject!.objectType,
            displayOptions: {...state.options.graphDisplayOptions},
            hideShowAdditional: true,
            hideShowStacked: true
        });

        if (result.reason === "ok") {
            const values = result.values as ObjectGraphOptionsDialogValues;
            setGraphOptions(values.displayOptions as ITrendingDisplayOptions);
        }
    };

    const onDateChanged = async (date: Date | null) => {
        if (date && canChangeDate) {

            const result = await canChangeDate();

            if (!result) {
                return;
            }

            setData({
                cols: [],
                rows: []
            });

            state.gridApi?.redrawRows();

            setSelectedDate(date);
            setEndDate(date);

            if (isPhone) {

                dispatchAppSettings({
                    type: AppSettingsActionTypeEnum.SideSheetOpenChanged,
                    open: false
                });
            }
    
        }
    }

    function onMinMaxChange(minValue: number | undefined, maxValue: number | undefined) {

        if (minValue !== undefined && maxValue !== undefined) {

            if (minValue >= maxValue) {
                setMinMaxError(true);
                return;
            } 
        } 

        setMinMaxError(false);
        setMinMaxY([minValue, maxValue]);
    }

    useEffect(() => {
        if (selectedDate && selectedDate.getTime() !==  state.options.selectedDate.getTime()) {

            setData({
                cols: [],
                rows: []
            });

            state.gridApi?.redrawRows();

            setSelectedDate(selectedDate);
            setEndDate(selectedDate);

        }
    }, [
        selectedDate,
        setSelectedDate,
        setEndDate,
        setData,
        state.options.selectedDate,
        state.gridApi
    ]);

    useEffect(() => {
        if (routeContext.navSrc !== NavigationSource.Breadcrumb && 
            routeContext.navSrc !== NavigationSource.ArrowPrev &&
            routeContext.navSrc !== NavigationSource.ArrowNext) {
            storageService.local.remove(OBJECT_VALUES_VIEW_OPTION_STORAGE_KEY);

            resetDurationOptions();
        }
    }, [
        routeContext.navSrc,
        resetDurationOptions
    ]);
    

    useEffect(() => {

        if (dataResponse && (dataResponse.objectType !== objectViewState?.contextObject?.objectType ||
            dataResponse.id !== objectViewState?.contextObject?.id)) {
                resetDurationOptions();
            }
    }, [
        dataResponse,
        objectViewState?.contextObject,
        resetDurationOptions,
    ]);

    useEffect(() => {
       
        const liveDataStateItem = getActiveStatus(routeContext.contextObject as IeLynxObject);
        setCommandInProgress(liveDataStateItem ? liveDataStateItem.commandComplete ? false : true : false)

    }, [
        dataResponse,
        getActiveStatus,
        setCommandInProgress,
        routeContext.contextObject
    ])

    useEffect(() => {
        if (dataResponse && !state.commandInProgress /*&& !state.inWriteDownMode*/) {

            // initialize original value for each tag
            dataResponse.tags.forEach((o) => {
                o.originalTagValue = o.tagValue;
                o.selected = false;
            });

            statusCount.current = 0;

            const liveDataStateItem = getActiveStatus(routeContext.contextObject as IeLynxObject);

            const defaultSummaryListingTags = isPhone && (userState.userSettings?.defaultDeviceView ?? false);
            
            const categories = ObjectTagValueUtils.getTagCategories(dataResponse, defaultSummaryListingTags);

            if (_.isEqual(state.categories.map(o => o.id), categories.map(o => o.id))) {

                state.categories.forEach((o/*, index*/) => {
                    const c = categories.find(v => v.id === o.id);
                    if (c) {
                        c.selected = o.selected
                    }
                });
            }


            let defaultConfigurationTATs: number[] = [];
            if (objectViewState) {
                defaultConfigurationTATs = (objectViewState as IGroupViewState).defaultConfigurationTATs;
            }

            const cols: Array<ColDef> = ObjectTagValueUtils.createGridColumns(theme,
                mode ?? ObjectTagValuesMode.Values,
                !isPhone,
                offlineState.offline,
                offlineConfig.useLKV,
                grouped,
                state.options.showTagDescriptions,
                configurationAllowed,
                (state.options.configurationID ?? 0) > 0,
                state.options.columnOptions,
                userState.user?.isElynxAdmin ?? false
            );

            const rows: Array<RowDef> = ObjectTagValueUtils.createGridRows(dataResponse,
                routeContext,
                mode ?? ObjectTagValuesMode.Values,
                offlineState.offline,
                liveDataStateItem,
                categories.filter(o => o.selected),
                defaultConfigurationTATs,
                isPhone,
                //state.inWriteDownMode
                false,
                userState.userSettings?.tankLevelInches ?? false);

            if (cols && rows) {
                setData({
                    cols: cols,
                    rows: rows
                });
            }

            if (!_.isEqual(state.categories, categories)) {
                setDefaultCategory(ObjectTagValueUtils.getDefaultTagCategory(dataResponse, defaultSummaryListingTags));
                setCategories(categories);
            }
        }
    }, [
        getActiveStatus,
        state.commandInProgress,
        state.inWriteDownMode,
        state.options.showTagDescriptions,
        grouped,
        offlineState.offline,
        dataResponse,
        routeContext,
        routeContext.groupID,
        objectViewState,
        setData,
        mode,
        theme,
        state.categories,
        setCategories,
        isPhone,
        userState.userSettings?.defaultDeviceView,
        startDataEntry,
        setDefaultCategory,
        setCommandInProgress,
        offlineConfig.useLKV,
        state.options.columnOptions,
        state.options.configurationID,
        configurationAllowed,
        userState.user?.isElynxAdmin,
        userState.userSettings?.tankLevelInches
    ]);


    // live data status in progress
    useEffect(() => {

        if (visible && state.gridApi) {
            const item = getActiveStatus(routeContext.contextObject as IeLynxObject);

            if (state.data && item) {
                doLiveData(item);
            }
        }
    }, [
        visible,
        state.data,
        getActiveStatus,
        state.gridApi,
        doLiveData,
        routeContext.contextObject,
    ]);

    useEffect(() => {
        if (state.inWriteDownMode && offlineState.offline) {
            exitWriteDownMode();

        }
    }, [
        state.inWriteDownMode,
        offlineState.offline,
        exitWriteDownMode
    ]);


    useEffect(() => {
        if (visible) {
            const viewOptionState = state.isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState?.(viewOptionState);
        }
    }, [
        visible,
        state.isOptionsDirty,
        setViewOptionState
    ]);

    const liveDataStatus = getActiveStatus(routeContext.contextObject as IeLynxObject);

        // this effect should run on every render to ensure the chart fills the view
    useEffect(() => {
        // must give time for the sidesheet transition to close
        // has completed
        if (isTank) {
            setTimeout(() => {
                tankLevelGraphRef.current?.reflow();
            }, 500);
        }
    });


    const progress = progressState.items.find(o => o.objectID === routeContext.contextObject!.id && o.objectType === routeContext.contextObject!.objectType); 

    useEffect(() => {

        if (progress?.notificationMsg.status === StatusEnum.Completed ||
            progress?.notificationMsg.status === StatusEnum.Failed) {
            setDownloadUid(undefined);
        }
    }, [
        routeContext.groupID,
        progress,
        state.downloadUid,
        setDownloadUid
    ]);

    useEffect(() => {
        if (!isPhone && !dense) {
            const currentConfigurationID = state.options.configurationID;
            const defaultConfigurationID = state.options.defaultConfigurationID;
            const hasConfigurations = (configurations?.configurations?.length ?? 0) > 0;
            const configuration = getConfigurationDescription(currentConfigurationID);
            const hasGroupModifyPermission = hasPermission(configurations?.userPermissions ?? [], writePermission);
            const hasConfigurationWritePermission = ((configuration?.access === AccessType.Private) ||
                hasPermission(configuration?.userPermissions ?? [], writePermission)) ?? false;

            let canEditConfiguration = false;
            let canDuplicateConfiguration = false;
            let canSetViewAsDefault = false;
            let canModifyConfigurationPermissions = false;
            let canDeleteConfiguration = false;
            let canRenameConfiguration = false;
            const canModify = hasConfigurations && !configuration?.internal; // can't modify if selected configuration is internal

            switch (configuration?.access) {

                case AccessType.Private:
                    canEditConfiguration = canModify;
                    canDuplicateConfiguration = canModify;
                    canSetViewAsDefault = currentConfigurationID !== defaultConfigurationID;
                    canModifyConfigurationPermissions = canModify;
                    canDeleteConfiguration = canModify;
                    canRenameConfiguration = canModify;
                    break;

                case AccessType.Shared:
                    canEditConfiguration = hasGroupModifyPermission && canModify;
                    canDuplicateConfiguration = canModify;
                    canSetViewAsDefault = currentConfigurationID !== defaultConfigurationID || hasGroupModifyPermission;
                    canModifyConfigurationPermissions = hasGroupModifyPermission && canModify;
                    canDeleteConfiguration = hasGroupModifyPermission && canModify;
                    canRenameConfiguration = hasGroupModifyPermission && canModify;
                    break;

                case AccessType.SharedReadOnly:
                    canEditConfiguration = hasConfigurationWritePermission && canModify;
                    canDuplicateConfiguration = canModify;
                    canSetViewAsDefault = currentConfigurationID !== defaultConfigurationID || hasGroupModifyPermission;
                    canModifyConfigurationPermissions = hasConfigurationWritePermission && canModify;
                    canDeleteConfiguration = hasConfigurationWritePermission && canModify;
                    canRenameConfiguration = hasConfigurationWritePermission && canModify;
                    break;

                default:
                    break;
            }

            const configurationActions = (configurationActionItems ?? [])
                .map(action => {
                    const actionCopy = { ...action };

                    switch (action.action) {
                        case ConfigurationActionEnum.Edit:
                            actionCopy.disabled = !canEditConfiguration;
                            break;

                        case ConfigurationActionEnum.CreateNew:
                            // should always be enabled
                            actionCopy.disabled = false;
                            break;

                        case ConfigurationActionEnum.Duplicate:
                            actionCopy.disabled = !canDuplicateConfiguration;
                            break;

                        case ConfigurationActionEnum.SetAsDefault:
                            actionCopy.disabled = !canSetViewAsDefault;
                            break;

                        case ConfigurationActionEnum.ModifyPermissions:
                            actionCopy.disabled = !canModifyConfigurationPermissions;
                            break;

                        case ConfigurationActionEnum.Delete:
                            actionCopy.disabled = !canDeleteConfiguration;
                            break;

                        case ConfigurationActionEnum.Rename:
                            actionCopy.disabled = !canRenameConfiguration;
                            break;
                    }

                    return actionCopy;
                });

            setConfigurationActions?.(configurationActions);
        }
    }, [
        configurations?.configurations?.length,
        configurations?.userPermissions,
        getConfigurationDescription,
        setConfigurationActions,
        state.options.configurationID,
        state.options.defaultConfigurationID,
        isPhone,
        dense
    ]);

    // Update the configuration id
    useEffect(() => {
        if (!dense) {
            const configID = routeContext.state?.configurationID;
            const defaultConfigID = configurations?.defaultConfigurationID ?? LEGACY_CONFIGURATION_ID;

            setDefaultConfigurationID(defaultConfigID);

            if (configID) {
                setConfigurationID(configID);
            } else {
                setConfigurationID(defaultConfigID);
            }
        }
    }, [
        configurations?.defaultConfigurationID,
        setConfigurationID,
        setDefaultConfigurationID,
        // This does not work correctly. Need entire routeContext.
        // routeContext.state?.configurationID,
        routeContext,
        dense
        // actions,
        // updateConfigurationActions,
        // configurations
    ]);
    
    // #region Configuration functions

    async function createView() {
        handleEditorResult(await configEditorDialog.open());
    }

    async function editView() {
        const configuration = getConfigurationDescription(state.options.configurationID as number);

        if (configuration) {

            if (configuration.access === AccessType.Shared ||
                configuration.access === AccessType.SharedReadOnly) {
                // if the currently selected configuration is shared or shared readonly, then
                // display a dialog warning that changes will affect other users
                const resultReason = await showEditSharedConfirmationPrompt();

                if (resultReason === "yes") {
                    await _duplicateView();
                } else if (resultReason === "no") {

                    // continue editing
                    handleEditorResult(await configEditorDialog.open({
                        configurationID: state.options.configurationID ?? LEGACY_CONFIGURATION_ID
                    }));
                }
            } else {

                handleEditorResult(await configEditorDialog.open({
                    configurationID: state.options.configurationID ?? LEGACY_CONFIGURATION_ID
                }));
            }
        }
    }

    async function duplicateView() {
        await _duplicateView();
    }

    async function modifyViewPermissions() {
        const configuration = getConfigurationDescription(state.options.configurationID as number);

        if (configuration) {

            if (configuration?.access === AccessType.Shared ||
                configuration?.access === AccessType.SharedReadOnly) {

                const shouldContinue = await showModifyPermissionsSharedConfirmationPrompt();

                if (!shouldContinue) {
                    return;
                }
            }

            await _modifyViewPermissions(configuration);
        }
    }

    async function deleteView() {

        const configuration = getConfigurationDescription(state.options.configurationID as number);

        if (configuration) {

            const shouldContinue = await showDeleteConfirmationPrompt(configuration.name ?? "");

            if (!shouldContinue) {
                return;
            }

            deleteConfiguration();
        }
    }

    async function renameView() {

        const configuration = getConfigurationDescription(state.options.configurationID as number);

        if (configuration) {

            if (configuration?.access === AccessType.Shared ||
                configuration?.access === AccessType.SharedReadOnly) {

                const shouldContinue = await showRenameSharedConfirmationPrompt();

                if (!shouldContinue) {
                    return;
                }
            }

            await _renameView(configuration);
        }
    }

    async function setDefaultView() {

        const hasGroupWritePermission = hasPermission(configurations?.userPermissions ?? [], writePermission);
        
        const configuration = getConfigurationDescription(state.options.configurationID as number);

        if (configuration) {

            if (configuration.access === AccessType.Private || !hasGroupWritePermission) {

                setDefaultConfiguration({
                    configurationID: configuration.id,
                    forCurrentUser: true,
                    forUsersWithoutDefault: false,
                    forAllUsers: false,
                    forAllObjectsInCurrentGroupWithoutDefault: false
                });

            } else {

                const defaultOptions = (await getDefaultOptions()).data;

                if (defaultOptions) {
                    const result = await defaultConfigurationDialog.open({
                        configurationName: configuration.name,
                        configurationType: t("configuration.types.objectTagValues"),
                        groupName: defaultOptions.groupName,
                        objectType: contextObject?.objectType,
                        objectName: defaultOptions.objectName,
                        forCurrentUser: defaultOptions.isForCurrentUser,
                        forUsersWithoutDefault: defaultOptions.isForUsersWithoutDefault,
                        forAllUsers: defaultOptions.isForAllUsers,
                        forAllObjectsInCurrentGroupWithoutDefault: defaultOptions.isForAllObjectsInCurrentGroupWithoutDefault,
                        configurationID: configuration.id
                    });

                    if (result.reason === 'ok' && result.values) {
                        setDefaultConfiguration({
                            configurationID: configuration.id,
                            forCurrentUser: result.values.forCurrentUser,
                            forUsersWithoutDefault: result.values.forUsersWithoutDefault,
                            forAllUsers: result.values.forAllUsers,
                            forAllObjectsInCurrentGroupWithoutDefault: result.values.forAllObjectsInCurrentGroupWithoutDefault ?? false
                        });
                    }
                }
            }
        }
    }

    async function _duplicateView() {
        handleEditorResult(await configEditorDialog.open({
            configurationID: state.options.configurationID ?? LEGACY_CONFIGURATION_ID,
            duplicate: true
        }));
    }

    async function _modifyViewPermissions(configuration: ObjectTagValuesConfigurationDescription) {
        
        const hasGroupWritePermission = hasPermission(configurations?.userPermissions ?? [], writePermission);
        const hasConfigurationWritePermission = ((configuration?.access === AccessType.Private) ||
            hasPermission(configuration?.userPermissions, writePermission)) ?? false;

        const result = await accessLevelDialog.open({
            access: configuration?.access,
            configurationType: t("configuration.types.objectTagValues"),
            hasGroupWrite: hasGroupWritePermission,
            hasConfigWrite: hasConfigurationWritePermission
        });

        if (result.reason === "ok" && result.values?.access) {

            modifyAccess(result.values.access);
        }
    }

    async function _renameView(configuration: ObjectTagValuesConfigurationDescription) {
        const result = await renameConfigurationDialog.open({
            configurationName: configuration?.name,
            configurationType: t("configuration.types.objectTagValues")
        });

        if (result.reason === "ok" && result.values) {

            renameConfiguration(result.values.configurationName);
        }
    }
    
    function handleEditorResult(result: ObjectTagValuesConfigurationEditorDialogResult) {
        if (result.reason === "saveAsNew" || result.reason === "saveChanges") {
            if (result.values?.configurationID !== state.options.configurationID) {
                setConfigurationID(result.values?.configurationID);
            } else {
                setData({
                    cols: [],
                    rows: []
                });
                setTimeout(() => refetch(), 250);
            }
        }
    }

    async function showEditSharedConfirmationPrompt(): Promise<ConfirmationDialogResultReason> {
        const result = await confirm({
            dialogTitle: t("configuration.editOrDuplicateConfirmation.title"),
            confirmationText: t("configuration.editOrDuplicateConfirmation.confirmationText", { configurationType: t("configuration.types.objectTagValues") }),
            yesButtonText: t("configuration.editOrDuplicateConfirmation.yesButtonText"),
            noButtonText: t("configuration.editOrDuplicateConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });

        return result;
    }

    async function showRenameSharedConfirmationPrompt(): Promise<boolean> {
        const result = await confirm({
            dialogTitle: t("configuration.renameConfirmation.title"),
            confirmationText: t("configuration.renameConfirmation.text"),
            yesButtonText: t("configuration.renameConfirmation.yesButtonText"),
            noButtonText: t("configuration.renameConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        });

        return result === "yes";
    }

    async function showModifyPermissionsSharedConfirmationPrompt(): Promise<boolean> {
        const result = await confirm({
            dialogTitle: t("configuration.accessLevelModifyConfirmation.title"),
            confirmationText: t("configuration.accessLevelModifyConfirmation.text"),
            yesButtonText: t("configuration.accessLevelModifyConfirmation.yesButtonText"),
            noButtonText: t("configuration.accessLevelModifyConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        });

        return result === "yes";
    }

    async function showDeleteConfirmationPrompt(name: string): Promise<boolean> {
        const result = await confirm({
            dialogTitle: t("configuration.deleteConfirmation.title", {
                configurationType: t("configuration.types.objectTagValues"),
                configurationName: name
            }),
            confirmationText: t("configuration.deleteConfirmation.confirmationText"),
            yesButtonText: t("configuration.deleteConfirmation.yesButtonText"),
            noButtonText: t("configuration.deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        });

        return result === "yes";
    }

    function onConfigurationSelected(value: number) {
        setConfigurationID(value);
    }

    // #endregion Configuration functions


    useEffect(() => {
        if (dense && dashboardComponent) {
            setConfigurationID(dashboardComponent.configurationID);
            setColumnOptions(options => ({ ...options, 
                showUIDColumn: dashboardComponent.showUIDColumn,
                showLiveDataColumn: dashboardComponent.showLiveDataColumn,
                showLookupListColumn: dashboardComponent.showLookupListColumn,
                showFormulaColumn: dashboardComponent.showFormulaColumn, 
            }));
        }
    }, [
        dense,
        dashboardComponent,
        setConfigurationID,
        setColumnOptions
        //setDefaultConfigurationID
    ]);
   

    useEffect(() => {

        if (visible && 
                (routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Device || 
                    routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tank) &&
                configurationAllowed && 
                headerAndActionsV2 && 
                !dense) {


            const groups= [{
                heading: '',
                options: [{
                    value: -1,
                    label: 'All Tags',
                    isDefault: state.options.defaultConfigurationID === LEGACY_CONFIGURATION_ID
                }]
                },{
                    heading: t("objectTagValues.viewList.shared"),
                    options: sharedConfigurations
                }, {
                    heading: t("objectTagValues.viewList.sharedReadOnly"),
                    options: sharedReadOnlyConfigurations

                }, {
                    heading: t("objectTagValues.viewList.private"),
                    options: privateConfigurations
                }];
                
                if (!_.isEqual(configurationsContextState.configurations, groups)) {
                    setContextConfigurations(groups);
                }
            }
    }, [
        visible,
        dense,
        configurationAllowed,
        headerAndActionsV2,
        sharedConfigurations,
        sharedReadOnlyConfigurations,
        privateConfigurations,
        t,
        state.options.defaultConfigurationID,
        setContextConfigurations,
        configurationsContextState.configurations,
        routeContext.contextObject?.objectType
    ]);

    useEffect(() => {
        if (visible && configurationAllowed && headerAndActionsV2 && !dense) {

            setContextConfigurationID(state.options.configurationID ?? -1);
        }

    }, [
        visible,
        configurationAllowed,
        dense,
        headerAndActionsV2,
        state.options.configurationID,
        setContextConfigurationID
    ]);

    useEffect(() => {

        if (visible && configurationAllowed && headerAndActionsV2) {
            const token = subscribe(CONFIGURATION_CHANGED_TOPIC, (message, data) => setConfigurationID((data as ConfigurationChangedMsg).configurationID));
    
            return () => {
                if (token) {
                    unsubscribe(token);
                }
            }
        }
    }, [
        visible,
        configurationAllowed,
        headerAndActionsV2,
        setConfigurationID
    ]);

        

    return (
        <>
            {visible &&
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                        overflow: "hidden",
                    }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>                                
                    <LoadingZone isLoading={status === "loading" || state.isRefreshing}>

                            <ContentContainer>

                            <ContentSection variant={variant} elevation={elevation}>

                            

                                <Box id='tagvalue-container'
                                    sx={
                                        isPhone || offlineState.offline
                                            ? {
                                                display: "flex",
                                                flexDirection: "column",
                                                flexGrow: 1,
                                                overflow: "hidden",
                                                overflowY: 'auto',
                                                "& .ag-row-group": {
                                                    paddingLeft: (theme) => `${theme.spacing(.5)}!important`
                                                }
                                            }
                                            : {
                                                display: "flex",
                                                flexDirection: "column",
                                                flexGrow: 1,
                                                overflow: "hidden"

                                            }
                                    }>

                                    {
                                        state.dataError && 
                                        <NoData 
                                            severity="error"
                                            title={undefined}
                                            text={t("objectTagValues.dataErrorText")} 
                                        />
                                    }

                            





                                    {!state.dataError && status !== "loading" && !offlineState.offline && !isPhone ?


                                        <Box sx={{ 
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: "100%",
                                            overflowY: isTank ? 'auto' : undefined
                                            }}>


                                            {
                                                ((!isTank && routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Device) || 
                                                (routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Well)) &&
                                                state.options.view === ObjectTagValuesViewEnum.Chart && mode !== ObjectTagValuesMode.ManualEntry ?
                                                <ObjectTagValuesChartList
                                                    valuesDataResponse={dataResponse}
                                                    dateRange={state.options.durationOptions.duration}
                                                    showAlarmSetpointThresholds={state.options.showAlarmSetpointThreshold}
                                                    showTagDescriptions={state.options.showTagDescriptions}
                                                    defaultConfigurationTATs={(objectViewState as IGroupViewState)?.defaultConfigurationTATs ?? []}
                                                    categories={categoryList}
                                                    minY={state.options.minY}
                                                    maxY={state.options.maxY}
                                                />
                                            :


                                            <>

                                             {
                                                isTank  &&

                                                <Stack
                                                    spacing={2}
                                                    direction={ {xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
                                                    sx={{
                                                        flexShrink: 0,
                                                        flexGrow: 0,
                                                        flexBasis: (dataResponse?.tags?.length ?? 0) < 5 ? '70%' : '60%'
                                                    }}>
                
                                                    <Paper sx={{
                                                            padding: 2,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            flex: `1 1 100%`,
                                                            '& + &': {
                                                                marginTop: 1
                                                            },
                                                            minHeight: 400
                                                        }} elevation={0}>
                                                        { 
                                                            <TankCylinderV2 tank={dataResponse as IEntityValueTankModel} />
                                                        }
                                                    </Paper>
                
                                                    <Box sx={{
                                                        padding: 2,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        flex: `1 1 100%`,
                                                        '& + &': {
                                                            marginTop: 1
                                                        },
                                                    }}>
                          
                                                        <TankLevelGraph
                                                            ref={tankLevelGraphRef} 
                                                            tank={dataResponse as IEntityValueTankModel}
                                                            dateRange={state.options.durationOptions.duration} 
                                                            displayOptions={state.options.graphDisplayOptions}
                                                            />
                                                    </Box>                                    
                
                                                </Stack>
                
                                             }

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    flexShrink: 1,
                                                    flexGrow: 1,
                                                    flexBasis: !isTank ? '100%' : (dataResponse?.tags?.length ?? 0) < 5 ? '20%' : '40%',
                                                    padding: 1
                                                }}>

                                                <DefaultGrid
                                                    onGridReady={onGridReady}
                                                    suppressMovableColumns={true}

                                                    // this is necessary for all column data to render in the test environment
                                                    suppressColumnVirtualisation={fromTest}
                                                    //suppressHorizontalScroll={true} => Bug 51745: Tag Values Grid Autosize Columns
                                                    //domLayout={autoHeight ? 'autoHeight' : undefined}
                                                    domLayout={isTank ? 'autoHeight' : autoHeight ? 'autoHeight' : undefined}

                                                    components={{
                                                        tagNameRenderer: TagNameRenderer,
                                                        tagValueRenderer: TagValueRenderer,
                                                        numericCellEditor: NumericCellEditor,
                                                        timeCellEditor: TimeCellEditor,
                                                        feetAndInchesCellEditor: FeetAndInchesCellEditor,
                                                        feetAndInchesMaskedCellEditor: FeetAndInchesMaskedCellEditor,
                                                        objectGroupingCellRenderer: ObjectGroupingCellRenderer
                                                    }}
                                                    rowData={state.data?.rows}
                                                    defaultColDef={{
                                                        sortable: false,
                                                        filter: "agMultiColumnFilter",
                                                        resizable: true,
                                                        editable: isCellEditable
                                                        //autoHeight: true
                                                    }}
                                                    columnDefs={state.data?.cols}
                                                    groupDisplayType="groupRows"
                                                    groupRowRendererParams={{
                                                        suppressCount: true,
                                                        innerRenderer: "objectGroupingCellRenderer",
                                                        //navigationObjects: state.navigationObjects
                                                        navigationObjects: navigationObjects
                                                    }}

                                                    // Don't think this is necessary if we're using groupDisplayType="groupRows"
                                                    autoGroupColumnDef={{
                                                        headerName: t("tag.label") as string,
                                                        sortable: true,
                                                    }}

                                                    // getRowHeight={onGetRowHeight}

                                                    //getRowNodeId={(data: any) => data.id}
                                                    getRowId={(params: GetRowIdParams) => params.data?.id}

                                                    suppressClickEdit={!state.inWriteDownMode && mode === ObjectTagValuesMode.Values}
                                                    singleClickEdit={state.inWriteDownMode || mode === ObjectTagValuesMode.ManualEntry}

                                                    groupDefaultExpanded={collapseTagGroups ? 0 : 1}
                                                    // animateRows={true} // => Handled in DefaultGrid
                                                    onCellValueChanged={onDataChanged}
                                                    
                                                    onCellEditingStopped={onEditingStopped}

                                                    onRowClicked={onDesktopRowClicked}      
                                                    //onCellKeyPress={onCellKeyPress}
                                                    //onCellKeyDown={onCellKeyDown}

                                                // isExternalFilterPresent = {!state.inWriteDownMode && mode === ObjectTagValuesMode.Values ? isExternalFilterPresent : undefined}
                                                // doesExternalFilterPass = {!state.inWriteDownMode && mode === ObjectTagValuesMode.Values ? doesExternalFilterPass : undefined }
                                                />

                                            </Box>

                                            </>
                                            }

                                        </Box>

                                        :
                                        null

                                    }

                                    {/* render for mobile or offline */}
                                    {!state.dataError && status !== "loading" && (offlineState.offline || isPhone) ?
                                        <Box sx={{ 
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: "100%" 
                                            }}>                                            


                                            {
                                                isTank && !offlineState.offline &&

                                                <Stack
                                                    id='mystack'
                                                    spacing={2}
                                                    direction='column'
                                                    sx={{
                                                        flexShrink: 0,
                                                        flexGrow: 0,
                                                        flexBasis: '100%'
                                                    }}>
                
                                                    <Paper id='mypaper' sx={{
                                                            padding: 2,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            flex: `1 1 100%`,
                                                            '& + &': {
                                                                marginTop: 1
                                                            },
                                                        }} elevation={0}>
                                                        {
                                                            <TankCylinderV2 tank={dataResponse as IEntityValueTankModel} />
                                                        }
                                                    </Paper>
                
                                                    <Box sx={{
                                                        padding: 2,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        flex: `1 1 100%`,
                                                        '& + &': {
                                                            marginTop: 1
                                                        },
                                                        minHeight: 350
                                                    }}>
                          
                                                        <TankLevelGraph
                                                            ref={tankLevelGraphRef} 
                                                            tank={dataResponse as IEntityValueTankModel}
                                                            dateRange={state.options.durationOptions.duration} 
                                                            displayOptions={state.options.graphDisplayOptions}
                                                            />
                                                    </Box>                                    
                
                                                </Stack>
                
                                             }


                                            <Box
                                                id='mymobilegrid'
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    flexShrink: 1,
                                                    flexGrow: 1,
                                                    position: 'relative',
                                                    flexBasis: 'auto',
                                                    padding: 1
                                                }}>                                            


                                                <DefaultGrid
                                                    onGridReady={onGridReady}
                                                    // onFirstDataRendered={onFirstDataRendered}
                                                    // this is necessary for all column data to render in the test environment
                                                    suppressColumnVirtualisation={true}
                                                    //suppressColumnVirtualisation={true}
                                                    suppressMovableColumns={true}

                                                    suppressRowVirtualisation={true}
                                                    //domLayout={autoHeight ? 'autoHeight' : undefined}
                                                    //domLayout='autoHeight'
                                                    suppressHorizontalScroll={true}

                                                    components={{
                                                        tagNameRenderer: TagNameRenderer,
                                                        tagValueRenderer: TagValueRenderer,
                                                        numericCellEditor: NumericCellEditor,
                                                        feetAndInchesCellEditor: FeetAndInchesCellEditor,
                                                        feetAndInchesMaskedCellEditor: FeetAndInchesMaskedCellEditor,
                                                        objectGroupingCellRenderer: ObjectGroupingCellRenderer
                                                    }}

                                                    suppressClickEdit={!state.inWriteDownMode && mode === ObjectTagValuesMode.Values}
                                                    //suppressClickEdit={true}
                                                    singleClickEdit={false}
                                                    //singleClickEdit={state.inWriteDownMode || mode === ObjectTagValuesMode.ManualEntry}

                                                    rowData={state.data?.rows}
// TODO: use memoized default                                                    
                                                    defaultColDef={{
                                                        sortable: false,
                                                        //autoHeight: true //this causes grid error
                                                        editable: isCellEditable,
                                                        //filter: true
                                                        filter: "agMultiColumnFilter",
                                                        //cellEditorPopup: true
                                                    }}
                                                    columnDefs={state.data?.cols}
                                                    groupDisplayType="groupRows"
                                                    groupRowRendererParams={{
                                                        suppressCount: true,
                                                        innerRenderer: "objectGroupingCellRenderer",
                                                        //navigationObjects: navigationObjects
                                                    }}
                                                    // autoGroupColumnDef only applies to groupDisplayType="singleColumn" | "multipleColumns"
                                                    // we're using groupDisplayType="groupRows"
                                                    // autoGroupColumnDef={{
                                                    //     headerName: t("tag.label") as string,
                                                    // }}
                                                    groupDefaultExpanded={collapseTagGroups ? 0 : 1}
                                                    animateRows={false}
                                                    //getRowNodeId={(data: any) => data.id}
                                                    getRowId={(params: GetRowIdParams) => params.data?.id}
                                                    // getRowHeight={onGetRowHeight}

                                                    onRowClicked={onRowClicked}

                                                    onCellValueChanged={onDataChanged}
                                                    onCellEditingStopped={onEditingStopped}
                                                    //onCellKeyPress={onCellKeyPress}
                                                    //onCellKeyDown={onCellKeyDown}


                                                    //postProcessPopup= {onPostProcessPopup}
                                                    onCellEditingStarted={onCellEditingStarted}
                                                    suppressContextMenu={true}

                                                    // - SPR 2024-09-11 - This causes the entire grid to scroll, including the header
                                                    // - SPR 2024-09-25 - domLayout needs to be set to autoHeight when ObjectTagValues
                                                    //                    is used in tank battery details, otherwise the grid will shrink
                                                    //                    down to zero height - Bug #52067
                                                    //domLayout={autoHeight ? 'autoHeight' : undefined}
                                                    domLayout={
                                                        isTank
                                                            ? 'autoHeight'
                                                            : autoHeight
                                                                ? 'autoHeight'
                                                                : undefined
                                                    }
                                                    ensureDomOrder={true}
                                                    autoSizeStrategy={isPhone
                                                        ? AutoSizeStrategyEnum.FitToGrid
                                                        : AutoSizeStrategyEnum.FitToCellContents
                                                    }
                                                />


                                            </Box>

                                        </Box>
                                        :
                                        null}

                                </Box>
                            </ContentSection>

                            {!dense && !noOptionsSideSheet &&
                                <OptionsSideSheet onReset={() => resetOptions()}>

                                    {configurationAllowed && (!headerAndActionsV2 || isPhone) &&
                                        <SideSheetGroupedSelect
                                            value={state.options.configurationID}
                                            label={t("objectTagValues.viewList.label")}
                                            placeholder={t("objectTagValues.viewList.placeholder")}
                                            placeholderValue={LEGACY_CONFIGURATION_ID}
                                            placeholderSelectEnabled={true}
                                            placeholderIsDefault={state.options.defaultConfigurationID === LEGACY_CONFIGURATION_ID}
                                            optionGroups={[{
                                                heading: t("objectTagValues.viewList.shared"),
                                                options: sharedConfigurations
                                            }, {
                                                heading: t("objectTagValues.viewList.sharedReadOnly"),
                                                options: sharedReadOnlyConfigurations
                                            }, {
                                                heading: t("objectTagValues.viewList.private"),
                                                options: privateConfigurations
                                            }]}
                                            onChange={value => {
                                                onConfigurationSelected(value);
                                            }}
                                        />                                    
                                    }

                                    {mode === ObjectTagValuesMode.ManualEntry &&
                                    
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flexGrow: 1,
                                            padding: theme.spacing(1),
                                            marginTop: isPhone ? theme.spacing(1) : undefined
                                        }}>
                    
                                            {/* <LocalizationProvider dateAdapter={AdapterDateFns}> */}
                                                <DefaultDatePicker
                                                    label={t('manualEntry.pickerLabel')}
                                                    value={state.options.selectedDate}
                                                    maxDate={new Date()}
                                                    slotProps={{ textField: { variant: 'filled' } }}
                                                    onChange={(value) => { setSelectedDate(value as Date);}}
                                                    onAccept={(date: Date | null) => onDateChanged(date)}
                                                />
                                            {/* </LocalizationProvider> */}
                                        </Box>
                                    }
                                    {!isPhone && !isTank && mode !== ObjectTagValuesMode.ManualEntry &&
                                        <SideSheetRadioGroup
                                            label={t('objectTagValues.selectView')}
                                            value={state.options.view}
                                            radioButtons={[{
                                                label: t('objectTagValues.graph'),
                                                ariaLabel: "graph view",
                                                value: ObjectTagValuesViewEnum.Chart
                                            },{
                                                label: t('objectTagValues.grid'),
                                                ariaLabel: "grid view",
                                                value: ObjectTagValuesViewEnum.Grid
                                            }]}
                                            onChange={onViewChanged}
                                        />
                                    }

                                    
                                    {mode !== ObjectTagValuesMode.ManualEntry && <SideSheetMultiSelect<ITagCategory>
                                        id="filter-tags"
                                        label={configurationAllowed
                                            ? t('objectTagValues.filterTagGroups')
                                            : t('category.title')
                                        }
                                        placeholder={configurationAllowed
                                            ? t('objectTagValues.tagGroups')
                                            : t('category.placeholder')
                                        }
                                        options={categoryList} //{state.categories}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={categoryList.filter(o => o.selected)} //{state.categories.filter(o => o.selected)}
                                        getOptionLabel={(option) => option.text}
                                        onChange={onCategoryChanged}
                                    />}

                                    {!isPhone && !isTank && mode !== ObjectTagValuesMode.ManualEntry &&
                                        <>
                                            {state.options.view === ObjectTagValuesViewEnum.Chart &&
                                                <>
                                                    <FormLabel component="legend">{t('customDuration.duration')}</FormLabel>
                                                    <Button variant='text'
                                                        startIcon={<DateRange/>}
                                                        sx={{justifyContent: 'flex-start'}}
                                                        onClick={onCustomDuration}>
                                                        {state.options.durationLabel}
                                                    </Button>

                                                    <SideSheetSwitch
                                                        label={t('objectTagValues.showAlarmSetpointThresholds')}
                                                        checked={state.options.showAlarmSetpointThreshold}
                                                        onChange={(checked) => {
                                                            setShowAlarmSetpointThreshold(checked);
                                                        }}
                                                    />


                                <SideSheetSwitch 
                                    label={t('trending.forceMinMax')}
                                    checked={state.options.forceMinMax}
                                    onChange={(checked) => setForceMinMax(checked)}
                                />


                                {state.options.forceMinMax && 
                                <Stack direction='column'>
                                    <SideSheetInput
                                        label="MinY"
                                        numeric={true}
                                        debounceDelay={1000}
                                        value={state.options.minY === undefined ? '' : state.options.minY.toString()}
                                        error={state.options.minMaxError}
                                        helperText={state.options.minMaxError ? "MinY cannot be greater than or equal to MaxY" : undefined}
                                        onChange={(value) =>
                                            onMinMaxChange(value === '' || value === null ? undefined : +value, state.options.maxY)
                                        }
                                        sx={{
                                            marginBottom: state.options.minMaxError ? 1 : 3
                                        }}
                                    />

                                    <SideSheetInput
                                        label="MaxY"
                                        numeric={true}
                                        debounceDelay={1000}
                                        value={state.options.maxY === undefined? '' : state.options.maxY.toString()}
                                        onChange={(value) =>
                                            onMinMaxChange(state.options.minY, value === '' || value === null ? undefined : +value)
                                        }
                                    />
                                        
                                    </Stack>}





                                                </>
                                            }

                                            <SideSheetSwitch
                                                label={t('objectTagValues.showTagDescriptions')}
                                                checked={state.options.showTagDescriptions}
                                                onChange={(checked) => {
                                                    setShowTagDescriptions(checked);
                                                }}
                                            />
                                        </>
                                    }

                                    {isTank && !offlineState.offline && mode !== ObjectTagValuesMode.ManualEntry &&
                                        <>

                                            <Button variant='text'
                                                startIcon={<DisplaySettings />}
                                                sx={{ justifyContent: 'flex-start' }}
                                                onClick={onOverrideSettings}>
                                                {t('trending.overrideOptions.title')}
                                            </Button>

                                            <FormLabel component="legend">{t('customDuration.duration')}</FormLabel>
                                            <Button variant='text'
                                                startIcon={<DateRange/>}
                                                sx={{justifyContent: 'flex-start'}}
                                                onClick={onCustomDuration}>
                                                {state.options.durationLabel}
                                            </Button>

                                            {!isPhone && <SideSheetSwitch
                                                label={t('objectTagValues.showTagDescriptions')}
                                                checked={state.options.showTagDescriptions}
                                                onChange={(checked) => {
                                                    setShowTagDescriptions(checked);
                                                }}
                                            />}
                                        </>
                                    }

                                    {configurationAllowed && state.options.columnOptions &&
                                        <>
                                            <FormLabel component="legend">{t('objectTagValues.columnOptions.label')}</FormLabel>
                                            <SideSheetCheckbox
                                                label={t('objectTagValues.columnOptions.showUIDColumn')}
                                                checked={state.options.columnOptions.showUIDColumn}
                                                onChange={(checked) => {
                                                    setColumnOptions(options => ({ ...options, showUIDColumn: checked }));
                                                }}
                                            />
                                            {userState.user?.isElynxAdmin
                                                ? <>
                                                    <SideSheetCheckbox
                                                        label={t('objectTagValues.columnOptions.showReadItemColumn')}
                                                        checked={state.options.columnOptions.showReadItemColumn}
                                                        onChange={(checked) => {
                                                            setColumnOptions(options => ({ ...options, showReadItemColumn: checked }));
                                                        }}
                                                    />
                                                    <SideSheetCheckbox
                                                        label={t('objectTagValues.columnOptions.showWriteItemColumn')}
                                                        checked={state.options.columnOptions.showWriteItemColumn}
                                                        onChange={(checked) => {
                                                            setColumnOptions(options => ({ ...options, showWriteItemColumn: checked }));
                                                        }}
                                                    />
                                                </>
                                                : <>
                                                    <SideSheetCheckbox
                                                        label={t('objectTagValues.columnOptions.showLiveDataColumn')}
                                                        checked={state.options.columnOptions.showLiveDataColumn}
                                                        onChange={(checked) => {
                                                            setColumnOptions(options => ({ ...options, showLiveDataColumn: checked }));
                                                        }}
                                                    />
                                                </>
                                            }
                                            <SideSheetCheckbox
                                                label={t('objectTagValues.columnOptions.showLookupListColumn')}
                                                checked={state.options.columnOptions.showLookupListColumn}
                                                onChange={(checked) => {
                                                    setColumnOptions(options => ({ ...options, showLookupListColumn: checked }));
                                                }}
                                            />
                                            <SideSheetCheckbox
                                                label={t('objectTagValues.columnOptions.showFormulaColumn')}
                                                checked={state.options.columnOptions.showFormulaColumn}
                                                onChange={(checked) => {
                                                    setColumnOptions(options => ({ ...options, showFormulaColumn: checked }));
                                                }}
                                            />
                                        </>
                                    }
                                </OptionsSideSheet>
                            }
                        </ContentContainer>

                    </LoadingZone>

                    </LocalizationProvider>
                </Box>
            }
            <ConfirmWriteDownDialog {...bindConfirmWriteDownDialog(reviewWriteDownDialog)} />
            <CustomDurationDialog
                        hideDefault={true}
                        value={state.options.durationOptions}
                        {...bindCustomDurationDialog(durationDialog)}
                    />
            {state.commandInProgress &&
                <Fab color="default"
                    //variant='extended'
                    //id="submit"
                    type="submit"
                    name="cancel"
                    disabled={liveDataStatus?.commandState === LiveDataCommandStateEnum.CancelingDemandScan || liveDataStatus?.commandState === LiveDataCommandStateEnum.CancelingWriteDown}
                    onClick={() => onCancelLiveDataCommand()}
                    aria-label="cancel command"
                    sx={{
                        position: "absolute",
                        bottom: theme.spacing(2),
                        right: theme.spacing(1)
                    }}>
                    <Undo />
                    {/* {t('actions.cancelCommand')} */}
                </Fab>
            }
            {state.commandInProgress === false && state.inWriteDownMode &&
                <>

                    <Fab color="default"
                        name="submit"
                        //variant='extended'
                        disabled={!state.writeDownTags.length}
                        onClick={() => {
                            state.gridApi?.stopEditing();
                            _onSubmitWriteDownDelayed();
                        }}
                        aria-label="submit changes"
                        sx={{
                            position: "absolute",
                            bottom: theme.spacing(10),
                            right: theme.spacing(1)
                        }}>
                        {/* <SaveAlt /> */}
                        <FontAwesomeIcon
                            icon={faSquareArrowUpRight}
                            //size="1x"
                            style={{
                                fontSize: '1.5rem'
                             }} 
                        />

                    </Fab>

                    <Fab color="default"
                        name="cancel"
                        //variant='extended'
                        onClick={() => exitWriteDownMode()}
                        aria-label="cancel changes"
                        sx={{
                            position: "absolute",
                            bottom: theme.spacing(2),
                            right: theme.spacing(1)
                        }}>
                        <Undo />
                    </Fab>


                </>
            }
            <ObjectGraphOptionsDialog
                //contextObjectType = {routeContext.contextObject?.objectType as eLynxObjectTypeEnum}
                {...bindObjectGraphOptionsDialog(optionsDialog)}
            />
            {/* {isPhone &&  */}
            <WriteDownFormDialog {...bindWriteDownFormDialog(writeDownFormDialog)} />
            {/* } */}
            {/* Configuration-related dialogs */}
            <ObjectTagValuesConfigurationEditorDialog {...bindObjectTagValuesConfigurationEditorDialog(configEditorDialog)} />
            <SetAsDefaultDialog {...bindDefaultConfigurationDialog(defaultConfigurationDialog)} />
            <RenameConfigurationDialog {...bindRenameConfigurationDialog(renameConfigurationDialog)} />
            <AccessLevelDialog {...bindAccessLevelDialog(accessLevelDialog)} />
        </>
    );
}

export default forwardRef(ObjectTagValues);